import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import * as XLSX from "xlsx";
import {
  uploadAutomationData,
  processAutomationData,
  downloadProcessedData,
  downloadProcessedDataViaS3, // ← NEW, manual-download only (Data Matching)
  downloadClassificationOutputFile, // ← NEW, manual-download only (Classification)
  checkCountData,
  getCustomerDropdown,
  uploadModelFileToS3,
  processE2EAutomation,
  prepareAutomationInput,
  checkEnrichmentE2E,
  checkE2EInput,
  presignInputFile, // ← NEW
  uploadFileToS3, // ← NEW
  finalizeInputFileUpload, // ← NEW
} from "../model/DataProcessingModel.jsx";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

const DataProcessingVM = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleToastOpen } = useToast();
  const uploadIntervalRef = useRef(null);
  const isVerifyingRef = useRef(false);

  const [activeStep, setActiveStep] = useState(1);
  const [tabValue, setTabValue] = useState(1);
  const [toolTabValue, setToolTabValue] = useState(2);
  const [selectedTool, setSelectedTool] = useState("Data Matching");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStats, setProcessingStats] = useState({
    processed: 0,
    total: 5000,
    timeElapsed: "0:00",
    timeRemaining: "4:30",
  });
  const processingStartTimeRef = useRef(null);
  const remainingSecondsRef = useRef(0);
  const [isMobile, setIsMobile] = useState(false);
  const [uniqueRecords, setUniqueRecords] = useState(0);
  const [processRecords, setProcessRecords] = useState(0);
  const [batchId, setBatchId] = useState(null);
  const [cOpen, setCOpen] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [data, setData] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [checkCountTimer, setCheckCountTimer] = useState(null);
  const [modelOptions, setModelOptions] = useState([]);
  const [enrichBatchId, setEnrichBatchId] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingPlantOptions, setIsLoadingPlantOptions] = useState(false);
  const [sampleDialogOpen, setSampleDialogOpen] = useState(false);
  const [selectedSampleType, setSelectedSampleType] = useState("");
  const [sampleDownloadLoading, setSampleDownloadLoading] = useState(false);
  const [isModelFileUploading, setIsModelFileUploading] = useState(false);
  const [uploadedModelS3Location, setUploadedModelS3Location] = useState(null);
  const [modelSelectionType, setModelSelectionType] = useState("existing");
  const [uploadedModelFile, setUploadedModelFile] = useState(null);
  const [showInputSubOptions, setShowInputSubOptions] = useState(false);
  const [selectedInputSubType, setSelectedInputSubType] = useState("");
  const [cachedModelOptions, setCachedModelOptions] = useState({});
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const plantOptions = useMemo(() => {
    const cached = cachedModelOptions[selectedTool];
    if (!cached) return [];
    return Array.isArray(cached) ? [] : (cached.plants ?? []);
  }, [cachedModelOptions, selectedTool]);

  // ─── Automation states ──────────────────────────────────────────────────────
  const [automationPlantId, setAutomationPlantId] = useState("");
  const [automationStartDate, setAutomationStartDate] = useState("");
  const [automationEndDate, setAutomationEndDate] = useState("");
  const [isAutomationProcessing, setIsAutomationProcessing] = useState(false);
  const [automationFileStats, setAutomationFileStats] = useState({
    totalRecords: 0,
    uniqueRecords: 0,
  });
  const [isAutomationComplete, setIsAutomationComplete] = useState(false);
  const [automationBatchId, setAutomationBatchId] = useState(null);
  const [isLoadingAutomationCount, setIsLoadingAutomationCount] =
    useState(false);
  const [automationModelId, setAutomationModelId] = useState("");
  const [automationModelOptions, setAutomationModelOptions] = useState([]);
  const [automationInputId, setAutomationInputId] = useState("");
  const [automationInputOptions, setAutomationInputOptions] = useState([]);
  const [classificationLangauge, setClassificationLangauge] = useState("");
  const [classificationLangaugeOptions, setClassificationLangaugeOptions] =
    useState([]);

  const modelSelectionOptions = [
    { label: "Upload Custom Model", value: "upload" },
    { label: "Use Existing Model", value: "existing" },
  ];

  const sampleTypeOptions = [
    { label: "Input Sample", value: "input" },
    { label: "Custom Sample", value: "custom" },
  ];

  const inputSubTypeOptions = [
    { label: "ICTSI", value: "ICTSI" },
    { label: "GCT", value: "GCT" },
  ];

  const isE2EToolWithMandatoryModel = ["Data Matching"].includes(selectedTool);

  const isAutomationReadyToFetch = () => {
    return Boolean(
      automationModelId ||
      automationInputId ||
      automationPlantId ||
      automationStartDate ||
      automationEndDate,
    );
  };

  const SOURCE_TABLE_MAP = {
    Enrichment: {
      "SQ01 GT00": "SQ01GT00",
      "SQ01 All-terminals": "SQ01ALL",
      "MPN-GT00": "SQ01GT00",
      "MPN-All Terminals": "SQ01ALL",
    },
    default: {
      "SQ01 GT00": "SQ01 GT00",
      "SQ01 All-terminals": "SQ01 All-terminals",
      "MPN-GT00": "SQ01 GT00",
      "MPN-All Terminals": "SQ01 All-terminals",
    },
  };

  const getSourceTable = (inputId, tool) => {
    if (!inputId) return null;
    const map = SOURCE_TABLE_MAP[tool] || SOURCE_TABLE_MAP["default"];
    return map[inputId] ?? inputId;
  };

  // ─── Fetch count for E2E automation tab ─────────────────────────────────────
  const handleFetchAutomationCount = async () => {
    if (isAutomationProcessing) return;

    if (!isAutomationReadyToFetch()) {
      handleToastOpen(
        "error",
        "Please fill in at least one filter before fetching",
      );
      return;
    }

    setIsLoadingAutomationCount(true);
    setIsAutomationComplete(false);
    setAutomationBatchId(null);
    setAutomationFileStats({ totalRecords: 0, uniqueRecords: 0 });

    try {
      const newBatchId = uuidv4();
      const payload = {
        batch_id: newBatchId,
        ...(automationPlantId ? { plant_id: automationPlantId } : {}),
        ...(automationStartDate ? { start_date: automationStartDate } : {}),
        ...(automationEndDate ? { end_date: automationEndDate } : {}),
        ...(automationModelId ? { model_name: automationModelId } : {}),
        ...(automationInputId ? { input_name: automationInputId } : {}),
        ...(selectedTool !== "Classification"
          ? {
              source_table:
                getSourceTable(automationInputId, selectedTool) ||
                "SQ01 All-terminals",
            }
          : automationInputId
            ? { source_table: "SQ01 All-terminals" }
            : {}),
        ...(classificationLangauge ? { language: classificationLangauge } : {}),
      };

      const prepareResponse = await prepareAutomationInput(
        selectedTool,
        payload,
      );

      if (prepareResponse && prepareResponse.status === "success") {
        const totalRecordsFound = prepareResponse.total_records || 0;
        setAutomationFileStats({
          totalRecords: totalRecordsFound,
          uniqueRecords: totalRecordsFound,
        });
        setAutomationBatchId(prepareResponse.batch_id || newBatchId);
        if (totalRecordsFound === 0)
          handleToastOpen(
            "warning",
            "No records found for the selected filters",
          );
        else
          handleToastOpen(
            "success",
            totalRecordsFound + " records loaded successfully",
          );
      } else {
        setAutomationFileStats({ totalRecords: 0, uniqueRecords: 0 });
        handleToastOpen("error", "Failed to fetch record count");
      }
    } catch (error) {
      console.error("Error fetching automation count:", error);
      setAutomationFileStats({ totalRecords: 0, uniqueRecords: 0 });
      handleToastOpen("error", error.message || "Error fetching record count");
    } finally {
      setIsLoadingAutomationCount(false);
    }
  };

  useEffect(() => {
    if (isAutomationProcessing) return;
    setAutomationBatchId(null);
    setAutomationFileStats({ totalRecords: 0, uniqueRecords: 0 });
    setIsAutomationComplete(false);
  }, [
    automationPlantId,
    automationStartDate,
    automationEndDate,
    automationModelId,
    automationInputId,
    classificationLangauge,
    selectedTool,
  ]);

  const handleModelSelectionTypeChange = (value) => {
    setModelSelectionType(value);
    if (value === "existing") {
      setUploadedModelFile(null);
      setUploadedModelS3Location(null);
    } else {
      setSelectedModel("");
    }
  };

  const handleModelFileSelection = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const validMimeTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const validExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (
      !validMimeTypes.includes(file.type) ||
      !validExtensions.includes(fileExtension)
    ) {
      handleToastOpen(
        "error",
        "Invalid model file type. Please upload an Excel (.xlsx or .xls) file.",
      );
      return;
    }
    setUploadedModelFile(file);
    setUploadedModelS3Location(null);
    handleToastOpen("success", `Model file selected: ${file.name}`);
  };

  const handleRequestSample = () => {
    if (selectedTool === "Data Matching") {
      setSampleDialogOpen(true);
      setSelectedSampleType("");
      setShowInputSubOptions(false);
      setSelectedInputSubType("");
    } else {
      downloadSampleFile();
    }
  };

  const handleSampleDialogClose = () => {
    setSampleDialogOpen(false);
    setSelectedSampleType("");
    setShowInputSubOptions(false);
    setSelectedInputSubType("");
    setSampleDownloadLoading(false);
  };

  const handleSampleTypeSelection = (value) => {
    setSelectedSampleType(value);
    if (value === "input" && selectedTool === "Data Matching") {
      setShowInputSubOptions(true);
      setSelectedInputSubType("");
    } else {
      setShowInputSubOptions(false);
      setSelectedInputSubType("");
    }
  };

  const handleInputSubTypeSelection = (value) => {
    setSelectedInputSubType(value);
  };

  const handleConfirmSampleDownload = () => {
    if (!selectedSampleType) return;
    if (
      selectedTool === "Data Matching" &&
      selectedSampleType === "input" &&
      !selectedInputSubType
    )
      return;
    setSampleDownloadLoading(true);
    setTimeout(() => {
      const sampleTypeToDownload =
        selectedSampleType === "input" && selectedTool === "Data Matching"
          ? selectedInputSubType
          : selectedSampleType;
      downloadSampleFile(sampleTypeToDownload);
      setSampleDownloadLoading(false);
      setSampleDialogOpen(false);
      setSelectedSampleType("");
      setShowInputSubOptions(false);
      setSelectedInputSubType("");
    }, 500);
  };

  // ─── E2E Automation tab: start processing ───────────────────────────────────
  const handleAutomationProcess = async () => {
    const lockedBatchId = automationBatchId;
    const lockedTotalRecords = automationFileStats.totalRecords;

    const requiredModel = ["Data Matching"];

    if (!automationModelId && requiredModel.includes(selectedTool)) {
      handleToastOpen("error", "Please select a model first");
      return;
    }
    if (!lockedBatchId) {
      handleToastOpen("error", "Please select filters first to load records");
      return;
    }
    if (lockedTotalRecords === 0) {
      handleToastOpen("warning", "No records found for the selected filters");
      return;
    }

    try {
      setIsAutomationComplete(false);
      setIsAutomationProcessing(true);
      setProcessingProgress(0);
      console.log("1");
      setProcessingStats({
        processed: 0,
        total: lockedTotalRecords,
        timeElapsed: "0:00",
        timeRemaining: "calculating...",
        chunkInfo:
          "Starting processing of " + lockedTotalRecords + " records...",
      });

      await processE2EWithChunking(
        selectedTool,
        lockedBatchId,
        lockedTotalRecords,
      );

      setIsAutomationComplete(true);

      if (selectedTool === "Enrichment") {
        handleViewHistory();
      }
    } catch (error) {
      console.error("Automation process error:", error);
      handleToastOpen("error", error.message || "Automation process failed");
      setIsAutomationComplete(false);
      setIsAutomationProcessing(false);
    }
  };

  // ─── MANUAL UPLOAD: chunked processing via /process_e2e ─────────────────────
  const processManualUploadWithChunking = async (
    toolToUse,
    processingBatchId,
    totalRecords,
    modelToUse,
  ) => {
    const CHUNK_SIZE = 200;
    const totalChunks = Math.ceil(totalRecords / CHUNK_SIZE);
    const startTime = Date.now();
    processingStartTimeRef.current = startTime;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000;

    let cumulativeProcessed = 0;
    let successfulChunks = 0;
    let failedChunks = 0;

    setProcessingProgress(0);
    console.log("2");
    setProcessingStats({
      processed: 0,
      total: totalRecords,
      timeElapsed: "0:00",
      timeRemaining: "calculating...",
      chunkInfo: `Starting... (0/${totalChunks} chunks)`,
    });

    for (let i = 0; i < totalChunks; i++) {
      const limit = i * CHUNK_SIZE;
      const offset = Math.min((i + 1) * CHUNK_SIZE, totalRecords);
      let chunkSucceeded = false;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const chunkPayload = {
            batch_id: processingBatchId,
            limit,
            offset,
          };

          const chunkResponse = await processE2EAutomation(
            toolToUse,
            chunkPayload,
          );

          if (
            chunkResponse?.status === "completed" ||
            chunkResponse?.records_saved > 0 ||
            chunkResponse?.records_processed > 0
          ) {
            chunkSucceeded = true;
            successfulChunks++;
            cumulativeProcessed = chunkResponse.end_record || offset;

            const elapsedMs = Date.now() - startTime;
            const elapsedMin = Math.floor(elapsedMs / 60000);
            const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);
            const timeElapsed = `${elapsedMin}:${elapsedSec < 10 ? "0" + elapsedSec : elapsedSec}`;

            let timeRemaining = "0:00";
            if (cumulativeProcessed > 0 && cumulativeProcessed < totalRecords) {
              const avgMs = elapsedMs / cumulativeProcessed;
              const remMs = avgMs * (totalRecords - cumulativeProcessed);
              const remMin = Math.floor(remMs / 60000);
              const remSec = Math.floor((remMs % 60000) / 1000);
              timeRemaining = `${remMin}:${remSec < 10 ? "0" + remSec : remSec}`;
            }

            const progress =
              cumulativeProcessed >= totalRecords
                ? 99
                : Math.max(
                    1,
                    Math.floor((cumulativeProcessed / totalRecords) * 100),
                  );

            setProcessingProgress(progress);
            console.log("3");
            setProcessingStats({
              processed: cumulativeProcessed,
              total: totalRecords,
              timeElapsed,
              timeRemaining,
              chunkInfo: `Chunk ${i + 1}/${totalChunks} done (${chunkResponse.records_saved ?? 0} saved)`,
            });
            break;
          } else {
            if (attempt === MAX_RETRIES) failedChunks++;
          }
        } catch (err) {
          const is409 =
            err?.response?.status === 409 ||
            err?.message?.includes("409") ||
            err?.message?.includes("already exists");

          if (is409) {
            chunkSucceeded = true;
            successfulChunks++;
            cumulativeProcessed = offset;
            break;
          }

          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY * attempt));
          } else {
            failedChunks++;
          }
        }
      }

      if (!chunkSucceeded) {
        setProcessingStats((prev) => ({
          ...prev,
          chunkInfo: `Chunk ${i + 1}/${totalChunks} failed after ${MAX_RETRIES} retries — continuing...`,
        }));
      }

      if (i < totalChunks - 1) {
        await new Promise((r) => setTimeout(r, 300));
      }
    }

    setProcessingStats((prev) => ({
      ...prev,
      chunkInfo: "Finalizing...",
    }));

    try {
      const checkPayload = { data: [{ folderId: processingBatchId }] };
      await checkCountData(checkPayload, toolToUse, modelToUse);
    } catch (err) {
      console.warn("input_check failed (non-blocking):", err.message);
    }

    const finalElapsedMs = Date.now() - startTime;
    const finalMin = Math.floor(finalElapsedMs / 60000);
    const finalSec = Math.floor((finalElapsedMs % 60000) / 1000);

    setProcessingProgress(100);
    console.log("4");
    setProcessingStats({
      processed: totalRecords,
      total: totalRecords,
      timeElapsed: `${finalMin}:${finalSec < 10 ? "0" + finalSec : finalSec}`,
      timeRemaining: "0:00",
      chunkInfo: `All ${totalChunks} chunks complete`,
    });

    if (failedChunks === 0) {
      handleToastOpen(
        "success",
        `Processing complete! ${totalRecords} records processed.`,
      );
    } else if (successfulChunks > 0) {
      handleToastOpen(
        "warning",
        `Completed with ${failedChunks} failed chunks. ${cumulativeProcessed}/${totalRecords} records processed.`,
      );
    } else {
      throw new Error("All chunks failed — processing unsuccessful.");
    }
  };

  // ─── E2E Automation tab: chunked processing loop ─────────────────────────────
  const processE2EWithChunking = async (
    selectedTool,
    batchId,
    totalRecords,
  ) => {
    try {
      if (totalRecords === 0) {
        handleToastOpen("warning", "No records found to process");
        setIsAutomationProcessing(false);
        return;
      }

      const CHUNK_SIZE = 20;
      const totalChunks = Math.ceil(totalRecords / CHUNK_SIZE);

      let cumulativeProcessedRecords = 0;
      let successfulChunks = 0;
      let failedChunks = 0;
      const startTime = Date.now();
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 2000;

      setProcessingProgress(0);
      console.log("5");
      setProcessingStats({
        processed: 0,
        total: totalRecords,
        timeElapsed: "0:00",
        timeRemaining: "calculating...",
        chunkInfo: "Starting... (0/" + totalChunks + " chunks)",
      });

      for (let i = 0; i < totalChunks; i++) {
        const limit = i * CHUNK_SIZE;
        const offset = Math.min((i + 1) * CHUNK_SIZE, totalRecords);
        const recordsInThisChunk = offset - limit;
        let chunkSucceeded = false;

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            const chunkPayload = {
              batch_id: batchId,
              limit,
              offset,
              ...(automationPlantId ? { plant_id: automationPlantId } : {}),
              ...(automationStartDate
                ? { start_date: automationStartDate }
                : {}),
              ...(automationEndDate ? { end_date: automationEndDate } : {}),
              ...(automationModelId ? { model_name: automationModelId } : {}),
              ...(automationInputId ? { input_name: automationInputId } : {}),
              ...(selectedTool !== "Classification"
                ? {
                    source_table:
                      getSourceTable(automationInputId, selectedTool) ||
                      "SQ01 All-terminals",
                  }
                : automationInputId
                  ? { source_table: automationInputId }
                  : {}),
              ...(classificationLangauge
                ? { language: classificationLangauge }
                : {}),
            };

            const chunkResponse = await processE2EAutomation(
              selectedTool,
              chunkPayload,
            );

            if (chunkResponse?.status === "already_processed") {
              successfulChunks++;
              cumulativeProcessedRecords = chunkResponse.end_record || offset;
              const elapsedMs = Date.now() - startTime;
              const elapsedMinutes = Math.floor(elapsedMs / 60000);
              const elapsedSeconds = Math.floor((elapsedMs % 60000) / 1000);
              const timeElapsed =
                String(elapsedMinutes) +
                ":" +
                (elapsedSeconds < 10
                  ? "0" + elapsedSeconds
                  : String(elapsedSeconds));
              const rawPct = (cumulativeProcessedRecords / totalRecords) * 100;
              const progress = Math.max(1, Math.min(99, Math.floor(rawPct)));
              setProcessingProgress(progress);
              console.log("6");
              setProcessingStats({
                processed: cumulativeProcessedRecords,
                total: totalRecords,
                timeElapsed,
                timeRemaining: "calculating...",
                chunkInfo:
                  "Chunk " +
                  (i + 1) +
                  "/" +
                  totalChunks +
                  " skipped (already processed)",
              });
              chunkSucceeded = true;
              break;
            } else if (
              chunkResponse &&
              (chunkResponse.status === "success" ||
                chunkResponse.status === "completed" ||
                chunkResponse.status === "processed" ||
                chunkResponse.status === "ok" ||
                chunkResponse.status === 200 ||
                chunkResponse.records_saved > 0 ||
                chunkResponse.records_processed > 0)
            ) {
              chunkSucceeded = true;
              successfulChunks++;

              const recordsSavedInChunk =
                chunkResponse.records_saved ||
                chunkResponse.records_processed ||
                recordsInThisChunk;

              const endRecordFromAPI = chunkResponse.end_record || offset;
              const totalInBatch =
                chunkResponse.total_records_in_batch || totalRecords;

              cumulativeProcessedRecords = endRecordFromAPI;

              const rawPct = (cumulativeProcessedRecords / totalInBatch) * 100;
              const progress =
                cumulativeProcessedRecords >= totalInBatch
                  ? 99
                  : Math.max(1, Math.floor(rawPct));
              setProcessingProgress(progress);

              const elapsedMs = Date.now() - startTime;
              const elapsedMinutes = Math.floor(elapsedMs / 60000);
              const elapsedSeconds = Math.floor((elapsedMs % 60000) / 1000);
              const timeElapsed =
                String(elapsedMinutes) +
                ":" +
                (elapsedSeconds < 10
                  ? "0" + elapsedSeconds
                  : String(elapsedSeconds));

              let timeRemaining = "0:00";
              if (
                cumulativeProcessedRecords > 0 &&
                cumulativeProcessedRecords < totalInBatch
              ) {
                const avgTimePerRecord = elapsedMs / cumulativeProcessedRecords;
                const remainingRecords =
                  totalInBatch - cumulativeProcessedRecords;
                const estimatedRemainingMs =
                  avgTimePerRecord * remainingRecords;
                const remainingMinutes = Math.floor(
                  estimatedRemainingMs / 60000,
                );
                const remainingSeconds = Math.floor(
                  (estimatedRemainingMs % 60000) / 1000,
                );
                timeRemaining =
                  String(remainingMinutes) +
                  ":" +
                  (remainingSeconds < 10
                    ? "0" + remainingSeconds
                    : String(remainingSeconds));
              }
              console.log("6");
              setProcessingStats({
                processed: cumulativeProcessedRecords,
                total: totalInBatch,
                timeElapsed,
                timeRemaining,
                chunkInfo:
                  "Chunk " +
                  (i + 1) +
                  "/" +
                  totalChunks +
                  " completed (" +
                  recordsSavedInChunk +
                  " records saved)",
                matchRate: chunkResponse.extraction_stats?.match_rate,
                processingSpeed: chunkResponse.extraction_stats?.overall_speed,
              });

              break;
            } else {
              failedChunks++;
              const elapsedMs = Date.now() - startTime;
              const elapsedMinutes = Math.floor(elapsedMs / 60000);
              const elapsedSeconds = Math.floor((elapsedMs % 60000) / 1000);
              const timeElapsed =
                String(elapsedMinutes) +
                ":" +
                (elapsedSeconds < 10
                  ? "0" + elapsedSeconds
                  : String(elapsedSeconds));
              console.log("7");
              setProcessingStats({
                processed: cumulativeProcessedRecords,
                total: totalRecords,
                timeElapsed,
                timeRemaining: "calculating...",
                chunkInfo:
                  "Chunk " +
                  (i + 1) +
                  "/" +
                  totalChunks +
                  " failed — continuing...",
              });
            }
          } catch (chunkError) {
            const is409 =
              chunkError?.response?.status === 409 ||
              chunkError?.message?.includes("409") ||
              chunkError?.message?.includes("Conflict") ||
              chunkError?.message?.includes("already exists") ||
              chunkError?.message?.includes("already processed");

            if (is409) {
              chunkSucceeded = true;
              successfulChunks++;
              cumulativeProcessedRecords = offset;
              const elapsedMs409 = Date.now() - startTime;
              const em409 = Math.floor(elapsedMs409 / 60000);
              const es409 = Math.floor((elapsedMs409 % 60000) / 1000);
              setProcessingProgress(
                Math.max(
                  1,
                  Math.min(
                    99,
                    Math.floor(
                      (cumulativeProcessedRecords / totalRecords) * 100,
                    ),
                  ),
                ),
              );
              console.log("8");
              setProcessingStats({
                processed: cumulativeProcessedRecords,
                total: totalRecords,
                timeElapsed:
                  String(em409) +
                  ":" +
                  (es409 < 10 ? "0" + es409 : String(es409)),
                timeRemaining: "calculating...",
                chunkInfo:
                  "Chunk " +
                  (i + 1) +
                  "/" +
                  totalChunks +
                  " already done (skipped)",
              });
              break;
            }

            if (attempt < MAX_RETRIES) {
              const waitMs = RETRY_DELAY * attempt;
              await new Promise((resolve) => setTimeout(resolve, waitMs));
            }
          }
        }

        if (!chunkSucceeded) {
          failedChunks++;
          const elapsedMsFail = Date.now() - startTime;
          const emFail = Math.floor(elapsedMsFail / 60000);
          const esFail = Math.floor((elapsedMsFail % 60000) / 1000);
          console.log("9");
          setProcessingStats({
            processed: cumulativeProcessedRecords,
            total: totalRecords,
            timeElapsed:
              String(emFail) +
              ":" +
              (esFail < 10 ? "0" + esFail : String(esFail)),
            timeRemaining: "calculating...",
            chunkInfo:
              "Chunk " +
              (i + 1) +
              "/" +
              totalChunks +
              " failed after " +
              MAX_RETRIES +
              " retries — continuing...",
          });
        }

        if (i < totalChunks - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      const finalElapsedMs = Date.now() - startTime;
      const finalMinutes = Math.floor(finalElapsedMs / 60000);
      const finalSeconds = Math.floor((finalElapsedMs % 60000) / 1000);
      const finalTimeElapsed =
        String(finalMinutes) +
        ":" +
        (finalSeconds < 10 ? "0" + finalSeconds : String(finalSeconds));

      setProcessingProgress(100);
      console.log("10");
      setProcessingStats({
        processed: totalRecords,
        total: totalRecords,
        timeElapsed: finalTimeElapsed,
        timeRemaining: "0:00",
        chunkInfo: "Completed all " + totalChunks + " chunks",
      });

      await new Promise((resolve) => setTimeout(resolve, 1000));

      checkE2EInput(selectedTool, batchId).catch((err) =>
        console.error("checkE2EInput failed (non-blocking):", err.message),
      );

      setIsAutomationProcessing(false);

      if (failedChunks === 0) {
        handleToastOpen(
          "success",
          "E2E Automation completed successfully! Processed all " +
            totalRecords +
            " records in " +
            totalChunks +
            " chunks.",
        );
      } else if (successfulChunks > 0) {
        handleToastOpen(
          "warning",
          "E2E Automation completed with " +
            failedChunks +
            " failed chunks out of " +
            totalChunks +
            ". Successfully processed " +
            cumulativeProcessedRecords +
            "/" +
            totalRecords +
            " records.",
        );
      } else {
        handleToastOpen(
          "error",
          "E2E Automation failed. No chunks were processed successfully.",
        );
      }
    } catch (error) {
      console.error("Error in E2E chunked processing:", error);
      setIsAutomationProcessing(false);
      handleToastOpen("error", error.message || "E2E processing failed");
    }
  };

  const handleAutomationDownload = async () => {
    if (!automationBatchId || !isAutomationComplete) {
      handleToastOpen("error", "No processed data available to download");
      return;
    }
    setCOpen(true);
    try {
      let modelForDownload;
      if (selectedTool === "Enrichment") {
        modelForDownload = automationInputId || "SQ01 All-terminals";
      } else {
        modelForDownload = automationModelId || selectedModel || "ICTSI";
      }

      const downloadData = await downloadProcessedData(
        selectedTool,
        automationBatchId,
        modelForDownload,
      );

      let rawData = "";
      const responseValue = downloadData?.base64Data || "";

      if (responseValue.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(responseValue);
          rawData = parsed.base64Data || parsed.data || parsed.file || "";
        } catch {
          rawData = responseValue;
        }
      } else {
        rawData = responseValue;
      }

      if (!rawData) {
        handleToastOpen(
          "error",
          "Download failed: server did not return file data.",
        );
        return;
      }

      const formatName = (name) => (name ? name.replace(/[\s-]+/g, "_") : "");
      const modelForFilename = automationModelId || selectedModel || "E2E";
      const filename =
        "E2E_" +
        formatName(selectedTool) +
        "_" +
        formatName(modelForFilename) +
        "_" +
        new Date()
          .toISOString()
          .replace(/[-:]/g, "")
          .slice(0, 15)
          .replace("T", "_") +
        ".xlsx";
      const binaryString = atob(rawData.trim().replace(/\s/g, ""));
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++)
        bytes[i] = binaryString.charCodeAt(i);
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
      handleToastOpen("success", "E2E Automation File Downloaded Successfully");
    } catch (error) {
      if (error.name !== "AbortError")
        handleToastOpen(
          "error",
          "Download Failed: " + (error.message || "Unknown error"),
        );
    } finally {
      setCOpen(false);
      setTimeout(() => handleViewHistory(), 500);
    }
  };

  const handleAutomationRefresh = () => {
    setAutomationPlantId("");
    setAutomationStartDate("");
    setAutomationEndDate("");
    setAutomationModelId("");
    setAutomationInputId("");
    setClassificationLangauge("");
    setClassificationLangaugeOptions([]);
    setAutomationFileStats({ totalRecords: 0, uniqueRecords: 0 });
    setIsAutomationComplete(false);
    setAutomationBatchId(null);
    handleToastOpen("info", "Automation filters cleared");
  };

  const downloadSampleFile = (sampleType = null) => {
    let data;
    let filename;
    if (selectedTool === "Data Matching") {
      if (sampleType === "GCT") {
        data = [
          {
            "eCatalog name":"306062-306070 104297",
            "Release ID": "29240",
            OEM: "90003589",
            Article:"",
            "Supplier name":"Masonlift",
            "Supplier code":"04",
            Drawing:"AIR INTAKE PLUMBING B SERIES MIRROR POST 5 OUTLET",
            Description:"PLUG 1/2-14 NPT BRASS HEX HEAD",
            Notes:"",
            QTY:"",
          },
          {
            "eCatalog name":"306062-306070 104297",
            "Release ID": "29240",
            OEM: "90005951",
            Article:"",
            "Supplier name":"Masonlift",
            "Supplier code":"04",
            Drawing:"AIR RESTRICTION INDICATOR INSTALLATION - FIREWALL MOUNT",
            Description:"BULKHEAD 1/8 NPT",
            Notes:"",
            QTY:"",
          },
        ];
        filename = "GCT_Data_Matching_Sample.xlsx";
      } else if (sampleType === "ICTSI") {
        data = [
          {
            Material: "MAT001",
            "Material Description": "Sample Material 1",
            Language: "EN",
            "Int. material no.": "INT001",
            Plnt: "P001",
            MPN: "MPN001",
            "Manufact.": "Manufacturer 1",
            "Matl Group": "G001",
          },
          {
            Material: "MAT002",
            "Material Description": "Sample Material 2",
            Language: "EN",
            "Int. material no.": "INT002",
            Plnt: "P002",
            MPN: "MPN002",
            "Manufact.": "Manufacturer 2",
            "Matl Group": "G002",
          },
        ];
        filename = "ICTSI_Data_Matching_Sample.xlsx";
      } else if (sampleType === "custom") {
        data = [
          {
            int_material_no: "INT001",
            material_description: "Sample Material Description 1",
            language: "EN",
            material_no: "MAT001",
            plant_id: "P001",
            mpn: "MPN001",
            mpn_clean: "MPN001_CLEAN",
            Manufacturer: "Sample Manufacturer A",
          },
        ];
        filename = "Data_Matching_Custom_Sample.xlsx";
      } else {
        data = [
          {
            Material: "MAT001",
            "Material Description": "Sample Material 1",
            Language: "EN",
            "Int. material no.": "INT001",
            Plnt: "P001",
            MPN: "MPN001",
            "Manufact.": "Manufacturer 1",
            "Matl Group": "G001",
          },
        ];
        filename = "Data_Matching_Sample.xlsx";
      }
    } else if (
      selectedTool === "Classification" ||
      selectedTool === "Mpn Segregation"
    ) {
      data = [
        { Material: "MAT001", "Material Description": "Sample Material 1" },
        { Material: "MAT002", "Material Description": "Sample Material 2" },
        { Material: "MAT003", "Material Description": "Sample Material 3" },
      ];
      filename =
        selectedTool === "Classification"
          ? "Classification_Input_Sample.xlsx"
          : "Mpn_Segregation_Sample.xlsx";
    } else if (selectedTool === "alternate Manufacturer") {
      data = [
        {
          Manufacturer: "Siemens",
          MPN: "3RH1140-1MB40-0KT0",
          "Material Description": "COUPLING RELAY F. AUXILIARY",
        },
        {
          Manufacturer: "Siemens",
          MPN: "3RT1015-1AB01-ZW95",
          "Material Description": "CONTACTOR, AC-3 3 KW/400 V,",
        },
      ];
      filename = "Alternate_Manufacturer_Sample.xlsx";
    } else if (selectedTool === "Enrichment") {
      data = [
        {
          "MATERIAL NO": "AC01-E023-002",
          "MATERIAL DESCRIPTION": "CPU-UNIT SAFETY CONTROLLER G9SP-N20S OMR",
          "MATERIAL DESCRIPTION 2": "",
          "MATERIAL DESCRIPTION 3": "",
          "Manufacturer Name 1": "OMRON",
          "Manufacturer Part Number 1": "G9SP-N20S",
          "Manufacturer Name 2": "",
          "Manufacturer Part Number 2": "",
          "Manufacturer Name 3": "",
          "Manufacturer Part Number 3": "",
          Commodity: "CPU Module",
          "Project Name": "VICT",
        },
      ];
      filename = "Enrichment_Input_Sample.xlsx";
    } else if (selectedTool === "Duplicate Check") {
      data = [
        {
          Material: "A04-010-04043",
          "Material Description": "RING, SEAL",
          "Name 1": "",
          "MPN 1": "",
          "Name 2": "",
          "MPN 2": "",
          "Name 3": "",
          "MPN 3": "",
          "Name 4": "",
          "MPN 4": "",
          "Name 5": "",
          "MPN 5": "",
        },
      ];
      filename = "Duplicate_Check_Input_Sample.xlsx";
    } else {
      data = [
        { ID: "001", Name: "Sample Item 1", Category: "A" },
        { ID: "002", Name: "Sample Item 2", Category: "B" },
      ];
      filename = "Sample_Template.xlsx";
    }
    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Data");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      handleToastOpen("success", `Sample template "${filename}" downloaded`);
    } catch (error) {
      handleToastOpen("error", "Failed to generate sample template");
    }
  };

  const permissionData = localStorage.getItem("permissionData") || "";
  const permissions = permissionData.split(",");

  const tabConfig = [
    {
      label: "MPN SEGREGATION",
      weight: 700,
      textSize: "0.875rem",
      permission: "menu_data_mpn_segregation",
    },
    {
      label: "alternate MANUFACTURER",
      weight: 700,
      textSize: "0.875rem",
      permission: "menu_data_mpn_segregation",
    },
    {
      label: "DUPLICATE CHECK",
      weight: 700,
      textSize: "0.875rem",
      permission: "menu_data_duplicate_check",
    },
    {
      label: "DATA MATCHING",
      weight: 700,
      textSize: "0.875rem",
      permission: "menu_data_matching",
    },
    {
      label: "CLASSIFICATION",
      weight: 700,
      textSize: "0.875rem",
      permission: "menu_data_classification",
    },
    {
      label: "ENRICHMENT",
      weight: 700,
      textSize: "0.875rem",
      permission: "menu_data_enrichment",
    },
  ];

  const getDefaultTab = () => {
    if (permissions.includes("menu_data_matching"))
      return { index: 3, tool: "Data Matching" };
    for (let i = 0; i < tabConfig.length; i++) {
      if (!tabConfig[i].disabled)
        return { index: i, tool: getToolNameFromIndex(i) };
    }
    return { index: 0, tool: null };
  };

  useEffect(() => {
    const storedTool = localStorage.getItem("selectedDataTool");
    if (storedTool) {
      const tabIndex = getToolTabIndexFromName(storedTool);
      if (tabIndex !== -1 && !tabConfig[tabIndex]?.disabled) {
        setToolTabValue(tabIndex);
        setSelectedTool(storedTool);
        localStorage.removeItem("selectedDataTool");
        return;
      }
    }
    const defaultTab = getDefaultTab();
    if (defaultTab.tool) {
      setToolTabValue(defaultTab.index);
      setSelectedTool(defaultTab.tool);
    }
  }, []);

  useEffect(() => {
    const loadModelOptions = async () => {
      setIsLoadingOptions(true);
      setIsLoadingPlantOptions(true);
      try {
        const options = await getModelOptions();
        setModelOptions(options);
      } catch (error) {
        console.error("Error loading model options:", error);
        setModelOptions([]);
      } finally {
        setIsLoadingOptions(false);
        setIsLoadingPlantOptions(false);
      }
    };
    if (selectedTool) {
      loadModelOptions();
      setAutomationModelId("");
      setAutomationInputId("");
      setAutomationModelOptions([]);
      setAutomationInputOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTool]);

  useEffect(() => {
    const loadAutomationModels = async () => {
      if (!selectedTool) {
        setAutomationModelOptions([]);
        setAutomationInputOptions([]);
        return;
      }
      try {
        const customerData = await getCustomerDropdown(selectedTool);
        setAutomationModelOptions(
          customerData?.models && Array.isArray(customerData.models)
            ? customerData.models
            : [],
        );
        setAutomationInputOptions(
          customerData?.input && Array.isArray(customerData.input)
            ? customerData.input
            : [],
        );
        if (selectedTool === "Classification")
          setClassificationLangaugeOptions(customerData?.lang || []);
        else setClassificationLangaugeOptions([]);
      } catch (error) {
        console.error("Error loading automation model/input options:", error);
        setAutomationModelOptions([]);
        setAutomationInputOptions([]);
        setClassificationLangaugeOptions([]);
      }
    };
    loadAutomationModels();
  }, [selectedTool]);

  const getToolNameFromIndex = (index) => {
    const toolNames = [
      "Mpn Segregation",
      "alternate Manufacturer",
      "Duplicate Check",
      "Data Matching",
      "Classification",
      "Enrichment",
    ];
    return toolNames[index] || null;
  };

  const getModelOptions = async () => {
    if (cachedModelOptions[selectedTool]) {
      const cached = cachedModelOptions[selectedTool];
      return Array.isArray(cached) ? cached : (cached.options ?? []);
    }

    if (selectedTool === "Duplicate Check") {
      setCachedModelOptions((prev) => ({
        ...prev,
        [selectedTool]: { options: [], plants: [] },
      }));
      return [];
    } else if (selectedTool === "Data Matching") {
      const result = [
        { id: 1, name: "ICTSI-GT00" },
        { id: 2, name: "ICTSI-All Terminals" },
        { id: 3, name: "GCT" },
      ];
      try {
        const customerData = await getCustomerDropdown(selectedTool);
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: {
            options: result,
            plants: customerData.plants || [],
          },
        }));
      } catch (error) {
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: { options: result, plants: [] },
        }));
      }
      return result;
    } else if (selectedTool === "Classification") {
      const result = [
        { id: 1, name: "ICTSI" },
        { id: 2, name: "ICTSI - IFS" },
      ];
      try {
        const customerData = await getCustomerDropdown(selectedTool);
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: {
            options: result,
            plants: Array.isArray(customerData.plants)
              ? customerData.plants
              : [],
          },
        }));
      } catch (error) {
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: { options: result, plants: [] },
        }));
      }
      return result;
    } else if (selectedTool === "Enrichment") {
      try {
        const customerData = await getCustomerDropdown(selectedTool);
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: {
            options: customerData.customers,
            plants: customerData.plants || [],
          },
        }));
        return customerData.customers;
      } catch (error) {
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: { options: [], plants: [] },
        }));
        return [];
      }
    } else if (selectedTool === "Mpn Segregation") {
      try {
        const customerData = await getCustomerDropdown(selectedTool);
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: {
            options: customerData.customers,
            plants: customerData.plants || [],
          },
        }));
        return customerData.customers;
      } catch (error) {
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: { options: [], plants: [] },
        }));
        return [];
      }
    } else if (selectedTool === "alternate Manufacturer") {
      const result = [{ id: 1, name: "ICTSI" }];
      try {
        const customerData = await getCustomerDropdown(selectedTool);
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: {
            options: result,
            plants: customerData.plants || [],
          },
        }));
      } catch (error) {
        setCachedModelOptions((prev) => ({
          ...prev,
          [selectedTool]: { options: result, plants: [] },
        }));
      }
      return result;
    } else {
      const result = [{ id: 1, name: "ICTSI" }];
      setCachedModelOptions((prev) => ({
        ...prev,
        [selectedTool]: { options: result, plants: [] },
      }));
      return result;
    }
  };

  useEffect(() => {
    const preloadDropdownOptions = async () => {
      const toolsToPreload = ["Mpn Segregation", "Enrichment"];
      for (const tool of toolsToPreload) {
        if (cachedModelOptions[tool]) continue;
        try {
          if (tool === "Enrichment" || tool === "Mpn Segregation") {
            const customerData = await getCustomerDropdown(tool);
            setCachedModelOptions((prev) => ({
              ...prev,
              [tool]: {
                options: customerData.customers,
                plants: customerData.plants || [],
              },
            }));
          } else if (tool === "Classification") {
            setCachedModelOptions((prev) => ({
              ...prev,
              [tool]: { options: [{ id: 1, name: "ICTSI" }], plants: [] },
            }));
          }
        } catch (error) {
          console.error(`Error pre-loading ${tool}:`, error);
        }
      }
    };
    const timer = setTimeout(preloadDropdownOptions, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("Timer Effect:", {
      isProcessing,
      startTime: processingStartTimeRef.current,
    });
    if (!isProcessing || !processingStartTimeRef.current) return;

    const timer = setInterval(() => {
      const elapsedSeconds = Math.floor(
        (Date.now() - processingStartTimeRef.current) / 1000,
      );

      const minutes = Math.floor(elapsedSeconds / 60);
      const seconds = elapsedSeconds % 60;

      if (isVerifyingRef.current) {
        setProcessingStats((prev) => ({
          ...prev,
          timeElapsed: `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`,
        }));
        return;
      }

      remainingSecondsRef.current = Math.max(
        0,
        remainingSecondsRef.current - 1,
      );

      const remainingMinutes = Math.floor(remainingSecondsRef.current / 60);
      const remainingSecs = remainingSecondsRef.current % 60;

      setProcessingStats((prev) => ({
        ...prev,
        timeElapsed: `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`,
        timeRemaining: `${remainingMinutes}:${remainingSecs < 10 ? "0" : ""}${remainingSecs}`,
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, [isProcessing]);

  const cardData = [
    {
      header: "Unique Records",
      value: `${uniqueRecords}/${totalCount || 0}`,
      id: "unique",
    },
    {
      header: "Processed Records",
      value: `${processRecords}/${totalCount || 0}`,
      id: "process",
    },
  ];

  const toolOptions = [
    {
      id: "mpn",
      name: "Mpn Segregation",
      disabled: false,
      tooltip: "Work In Progress",
    },
    {
      id: "manufacturer",
      name: "alternate Manufacturer",
      disabled: false,
      tooltip: "Work In Progress",
    },
    {
      id: "check",
      name: "Duplicate Check",
      disabled: false,
      tooltip: "Work In Progress",
    },
    { id: "match", name: "Data Matching", disabled: false },
    { id: "classification", name: "Classification", disabled: false },
    { id: "enrichment", name: "Enrichment", disabled: false },
  ];

  const getToolTabIndexFromName = (toolName) => {
    if (!toolName) return 2;
    const index = toolOptions.findIndex(
      (tool) => tool.name.toLowerCase() === toolName.toLowerCase().trim(),
    );
    return index !== -1 ? index : 2;
  };

  const isModelSelected = () => {
    if (selectedTool === "Data Matching") {
      return modelSelectionType === "existing"
        ? Boolean(selectedModel)
        : Boolean(uploadedModelFile && uploadedModelS3Location);
    }
    if (selectedTool === "Enrichment") return Boolean(selectedCustomer);
    if (selectedTool === "Duplicate Check") return true;
    return Boolean(selectedModel);
  };

  const clearAllIntervals = () => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    if (checkCountTimer) {
      clearTimeout(checkCountTimer);
      setCheckCountTimer(null);
    }
  };

  const pollUploadStatus = async (
    batchId,
    selectedTool,
    selectedModel,
    maxAttempts = 60,
    intervalMs = 10000,
  ) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        const mockData = {
          data: [{ folderId: batchId, activityType: selectedTool }],
        };
        const statusResponse = await checkCountData(
          mockData,
          selectedTool,
          selectedModel,
        );
        if (
          statusResponse &&
          (statusResponse.status === "pending" ||
            statusResponse.input_count > 0)
        ) {
          return { success: true, data: statusResponse };
        }
      } catch (error) {
        console.log(`Polling attempt ${attempt + 1} - waiting for backend...`);
      }
    }
    throw new Error(
      "Upload verification timeout. Please check history to confirm if file was uploaded.",
    );
  };
  // ─── Data Matching manual upload: chunked /input_file calls to stay under
  // API Gateway's 29s timeout, regardless of total file size ──────────────────
  const finalizeInputFileWithChunking = async ({
    s3Key,
    batchId,
    toolName,
    modelName,
    uniqueRecords,
    totalRecords,
    onProgress,
  }) => {
    const CHUNK_SIZE = 30000; // tune this if 7.5x`k still risks the 29s ceiling on your DB
    const totalChunks = Math.max(1, Math.ceil(totalRecords / CHUNK_SIZE));

    let lastResult = null;

    for (let i = 0; i < totalChunks; i++) {
      const limit = i * CHUNK_SIZE;
      const offset = Math.min(limit + CHUNK_SIZE, totalRecords);

      const result = await finalizeInputFileUpload({
        s3Key,
        batchId,
        toolName,
        modelName,
        uniqueRecords,
        totalRecords,
        limit,
        offset,
      });

      lastResult = result;

      if (onProgress) {
        onProgress({
          chunkIndex: i + 1,
          totalChunks,
          recordsInsertedThisChunk: result.records_inserted_this_chunk,
          startRecord: limit,
          endRecord: offset,
          hasMore: result.has_more,
        });
      }

      // backend already tells us if there's more to do — stop if not
      if (!result.has_more) break;
    }

    return lastResult; // final response, contains batch_id etc.
  };

  const handleFileUpload = async () => {
    try {
      clearAllIntervals();
      setIsUploading(true);
      setUploadProgress(0);
      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        await validateFile(selectedFile);
      } catch (validationError) {
        clearAllIntervals();
        setIsUploading(false);
        handleToastOpen("error", validationError.message || validationError);
        return;
      }

      let currentUniqueRecords = uniqueRecords;
      let currentTotalCount = totalCount;

      if (!currentUniqueRecords || currentUniqueRecords === 0) {
        try {
          currentUniqueRecords = await countUniqueRecords(selectedFile);
        } catch (error) {
          clearAllIntervals();
          setIsUploading(false);
          handleToastOpen("error", "Failed to count unique records");
          return;
        }
      }

      if (!currentTotalCount || currentTotalCount === 0) {
        try {
          const totalCountResult = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                  header: 1,
                });
                const rowsWithoutHeader = jsonData
                  .slice(1)
                  .filter((row) => row.length > 0);
                console.log(
                  "rowsWithoutHeader.length",
                  rowsWithoutHeader.length,
                );

                setTotalCount(rowsWithoutHeader.length);
                console.log("setTotalCount #1");

                resolve(rowsWithoutHeader.length);
              } catch (error) {
                reject(error);
              }
            };
            reader.onerror = () => reject("Error reading file");
            reader.readAsArrayBuffer(selectedFile);
          });
          currentTotalCount = totalCountResult;
        } catch (error) {
          clearAllIntervals();
          setIsUploading(false);
          handleToastOpen("error", "Failed to calculate total record count");
          return;
        }
      }

      let modelToUse;
      if (selectedTool === "Data Matching")
        modelToUse =
          modelSelectionType === "existing" ? selectedModel : "Custom Model";
      else if (selectedTool === "Enrichment") modelToUse = selectedCustomer;
      else if (selectedTool === "Duplicate Check") modelToUse = "ICTSI";
      else modelToUse = selectedModel;

      // ═══════════════════════════════════════════════════════════════════
      // MPN SEGREGATION: presign → S3 PUT → chunked ingest with REAL progress
      // ═══════════════════════════════════════════════════════════════════
      if (selectedTool === "Mpn Segregation") {
        const startTime = Date.now();
        const token =
          localStorage.getItem("token") || sessionStorage.getItem("token");

        try {
          // Step 1: get presigned S3 URL
          const presignResp = await axios.post(
            `https://devai.conasservices.com/v1/uat_mpn/mpn_segregation/input_file/presign`,
            {
              filename: selectedFile.name,
              tool_name: selectedTool,
              model_name: modelToUse,
            },
            {
              headers: {
                "Content-Type": "application/json",
                "x-api-key": token,
              },
            },
          );

          const { upload_url, s3_key, batch_id, content_type } =
            presignResp.data;
          setBatchId(batch_id);

          // Step 2: upload file directly to S3
          await axios.put(upload_url, selectedFile, {
            headers: { "Content-Type": content_type },
          });
          console.log("11");

          setProcessingStats({
            processed: 0,
            total: currentTotalCount,
            timeElapsed: "0:00",
            timeRemaining: "calculating...",
            chunkInfo: "Starting ingest...",
          });

          // Step 3: chunked ingest — drives the real loader, chunk by chunk
          const CHUNK_SIZE = 500;
          let limit = 0;
          let offset = CHUNK_SIZE;
          let hasMore = true;
          let finalResult = null;

          while (hasMore) {
            const chunkResp = await axios.post(
              `https://devai.conasservices.com/v1/uat_mpn/mpn_segregation/input_file/ingest_from_s3`,
              { batch_id, s3_key, limit, offset },
              {
                headers: {
                  "Content-Type": "application/json",
                  "x-api-key": token,
                },
              },
            );

            const result = chunkResp.data;
            finalResult = result;
            hasMore = result.has_more;

            const elapsedMs = Date.now() - startTime;
            const elapsedMin = Math.floor(elapsedMs / 60000);
            const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);

            setUploadProgress(
              Math.min(99, Math.floor(result.progress_percentage)),
            );
            console.log("12");
            setProcessingStats({
              processed: result.end_record,
              total: result.total_records_in_file,
              timeElapsed: `${elapsedMin}:${elapsedSec < 10 ? "0" + elapsedSec : elapsedSec}`,
              timeRemaining: hasMore ? "calculating..." : "0:00",
              chunkInfo: `Ingested ${result.start_record}-${result.end_record} of ${result.total_records_in_file}`,
            });

            if (hasMore) {
              limit = offset;
              offset = Math.min(
                offset + CHUNK_SIZE,
                result.total_records_in_file,
              );
            }
          }

          setUploadProgress(100);
          setTimeout(() => {
            setIsUploading(false);
            setData({
              status: 201,
              data: [
                {
                  folderId: batch_id,
                  activityType: selectedTool,
                  s3Location: s3_key,
                  key: s3_key,
                },
              ],
            });
            setCurrentFolderId(batch_id);
            console.log("13");
            setProcessingStats({
              processed:
                finalResult?.total_records_in_file || currentTotalCount,
              total: finalResult?.total_records_in_file || currentTotalCount,
              timeElapsed: processingStats.timeElapsed || "0:00",
              timeRemaining: "0:00",
            });
            handleToastOpen("success", "File Uploaded Successfully");
            setFileUploaded(true);
            setActiveStep(2);
          }, 500);
          return;
        } catch (ingestError) {
          clearAllIntervals();
          setIsUploading(false);
          const detail =
            ingestError.response?.data?.detail ||
            ingestError.message ||
            "Ingestion failed";
          handleToastOpen(
            "error",
            typeof detail === "string" ? detail : "Ingestion failed",
          );
          return;
        }
      }

      // ═══════════════════════════════════════════════════════════════════
      // ALL OTHER TOOLS: original fake-timer + single-call upload flow
      // ═══════════════════════════════════════════════════════════════════
      const startTime = Date.now();
      const recordCount = totalCount || 0;
      const uploadTimeoutDuration =
        recordCount <= 10000
          ? 10 * 60 * 1000
          : recordCount <= 20000
            ? 15 * 60 * 1000
            : 20 * 60 * 1000;

      console.log("14");
      if (selectedTool !== "Enrichment") {
        uploadIntervalRef.current = setInterval(() => {
          const elapsedMs = Date.now() - startTime;
          const progressPercentage = Math.min(
            90,
            (elapsedMs / uploadTimeoutDuration) * 100,
          );

          setUploadProgress((prevProgress) =>
            Math.max(prevProgress, Math.floor(progressPercentage)),
          );

          const elapsedSeconds = Math.floor(elapsedMs / 1000);
          const elapsedMinutes = Math.floor(elapsedSeconds / 60);
          const remainingSeconds = Math.floor(
            (uploadTimeoutDuration - elapsedMs) / 1000,
          );
          const remainingMinutes = Math.floor(remainingSeconds / 60);

          setProcessingStats({
            processed: Math.floor(
              (totalCount || 0) * (progressPercentage / 100),
            ),
            total: totalCount || 0,
            timeElapsed: `${elapsedMinutes}:${(elapsedSeconds % 60)
              .toString()
              .padStart(2, "0")}`,
            timeRemaining: `${remainingMinutes}:${(remainingSeconds % 60)
              .toString()
              .padStart(2, "0")}`,
          });
        }, 800);
      }

      let modelFileInfo = null;
      if (selectedTool === "Data Matching" && modelSelectionType === "upload") {
        if (!uploadedModelS3Location) {
          handleToastOpen("error", "Please upload the model file first");
          clearAllIntervals();
          setIsUploading(false);
          return;
        }
        modelFileInfo = uploadedModelS3Location;
      }

      let batchIdToUse;
      if (modelFileInfo) {
        batchIdToUse = modelFileInfo.batch_id;
        setBatchId(modelFileInfo.batch_id);
      } else {
        batchIdToUse = uuidv4();
        setBatchId(batchIdToUse);
      }

      let response;

      if (selectedTool === "Data Matching") {
        // ── presigned S3 upload + chunked finalize (avoids 413 on upload AND
        // 504 on /input_file processing, regardless of file size) ──────────
        try {
          const {
            upload_url,
            s3_key,
            batch_id: presignBatchId,
          } = await presignInputFile(selectedFile.name, selectedTool);

          await uploadFileToS3(selectedFile, upload_url, (pct) => {
            // S3 upload occupies the first 50% of the progress bar
            const scaledPct = Math.floor(pct * 0.5);
            setUploadProgress(scaledPct);
            setProcessingStats((prev) => ({
              ...prev,
              processed: Math.floor(
                (currentTotalCount || 0) * (scaledPct / 100),
              ),
              total: currentTotalCount || 0,
            }));
          });

          const finalizeResult = await finalizeInputFileWithChunking({
            s3Key: s3_key,
            batchId: batchIdToUse || presignBatchId,
            toolName: selectedTool,
            modelName: modelToUse,
            uniqueRecords: currentUniqueRecords,
            totalRecords: currentTotalCount,
            onProgress: ({ chunkIndex, totalChunks, endRecord }) => {
              // DB-insert phase occupies the remaining 50% of the progress bar
              const chunkProgress = Math.floor((chunkIndex / totalChunks) * 50);
              const scaledPct = 50 + chunkProgress;
              setUploadProgress(scaledPct);
              setProcessingStats((prev) => ({
                ...prev,
                processed: endRecord,
                total: currentTotalCount || 0,
                chunkInfo: `Saving records: chunk ${chunkIndex}/${totalChunks} (${endRecord}/${currentTotalCount})`,
              }));
            },
          });

          response = {
            status: finalizeResult.status_code,
            data: [
              {
                originalFile: selectedFile.name,
                fileSize: selectedFile.size,
                fileType: selectedFile.type,
                s3Location: s3_key,
                key: s3_key,
                folderId: finalizeResult.batch_id,
                activityType: selectedTool,
              },
            ],
          };

          setBatchId(finalizeResult.batch_id);
        } catch (uploadError) {
          clearAllIntervals();
          setIsUploading(false);

          const errDetail = uploadError.response?.data?.detail;
          if (
            errDetail &&
            (errDetail.missing_columns ||
              errDetail.expected_columns ||
              errDetail.found_columns)
          ) {
            setErrorDetails({
              message: errDetail.message || "Invalid file format",
              missing_columns: errDetail.missing_columns || [],
              expected_columns: errDetail.expected_columns || [],
              extra_columns: errDetail.extra_columns || [],
              found_columns: errDetail.found_columns || [],
            });
            setShowErrorModal(true);
          } else {
            handleToastOpen(
              "error",
              uploadError.message || "Upload failed. Please try again.",
            );
          }
          return;
        }
      } else {
        // ── EXISTING behavior for all other tools, unchanged ───────────────
        let needsPolling = false;
        try {
          response = await uploadAutomationData(
            [selectedFile],
            selectedTool,
            modelToUse,
            currentUniqueRecords,
            currentTotalCount,
            batchIdToUse,
          );
        } catch (uploadError) {
          const isTimeoutError =
            uploadError.code === "ERR_NETWORK" ||
            uploadError.code === "ERR_FAILED" ||
            uploadError.code === "ECONNABORTED" ||
            uploadError.message?.includes("timeout") ||
            uploadError.message?.includes("Gateway timeout") ||
            uploadError.response?.status === 504 ||
            uploadError.response?.status === 502;
          if (isTimeoutError) {
            needsPolling = true;
            response = {
              status: 201,
              data: [
                {
                  originalFile: selectedFile.name,
                  fileSize: selectedFile.size,
                  fileType: selectedFile.type,
                  s3Location:
                    selectedTool === "Mpn Segregation"
                      ? null
                      : `pending-${batchIdToUse}`,
                  key:
                    selectedTool === "Mpn Segregation"
                      ? null
                      : `pending-${batchIdToUse}`,
                  folderId: batchIdToUse,
                  activityType: selectedTool,
                  databaseOnly: selectedTool === "Mpn Segregation",
                },
              ],
            };
          } else {
            clearAllIntervals();
            const errorData =
              uploadError &&
              uploadError.message &&
              (uploadError.missing_columns ||
                uploadError.expected_columns ||
                uploadError.found_columns)
                ? uploadError
                : (uploadError.response?.data?.detail ?? null);
            if (errorData) {
              setErrorDetails({
                message: errorData.message || "Invalid file format",
                missing_columns: errorData.missing_columns || [],
                expected_columns: errorData.expected_columns || [],
                extra_columns: errorData.extra_columns || [],
                found_columns: errorData.found_columns || [],
              });
              setShowErrorModal(true);
            } else {
              handleToastOpen(
                "error",
                uploadError.message || "Upload failed. Please try again.",
              );
            }
            setIsUploading(false);
            return;
          }
        }

        if (needsPolling) {
          try {
            await pollUploadStatus(batchIdToUse, selectedTool, modelToUse);
          } catch (pollError) {
            clearAllIntervals();
            setIsUploading(false);
            handleToastOpen(
              "error",
              "Upload verification timeout. Please check history to confirm if file was uploaded.",
            );
            return;
          }
        }
      }

      clearAllIntervals();
      setUploadProgress(100);
      setTimeout(() => {
        setIsUploading(false);
        if (response?.status >= 200 && response?.status < 300) {
          setData(response);
          setCurrentFolderId(response.data[0].folderId);
          console.log("15");
          setProcessingStats({
            processed: totalCount,
            total: totalCount,
            timeElapsed: processingStats.timeElapsed || "0:00",
            timeRemaining: "0:00",
          });
          handleToastOpen("success", "File Uploaded Successfully");
          setFileUploaded(true);
          setActiveStep(2);
        } else {
          handleToastOpen(
            "error",
            "Upload failed with status: " + (response?.status || "unknown"),
          );
        }
      }, 500);
    } catch (error) {
      clearAllIntervals();
      if (
        error.message?.includes("CORS") ||
        error.message?.includes("Access-Control-Allow-Origin")
      ) {
        handleToastOpen("error", "CORS policy error. Please contact support.");
        setIsUploading(false);
        return;
      }
      const errorData =
        error &&
        error.message &&
        (error.missing_columns || error.expected_columns || error.found_columns)
          ? error
          : (error.response?.data?.detail ?? null);
      if (errorData) {
        const tryDecodeBase64 = (str) => {
          if (typeof str !== "string") return str;
          if (/^[A-Za-z0-9+/=]+$/.test(str) && str.length > 20) {
            try {
              return atob(str);
            } catch (e) {
              return str;
            }
          }
          return str;
        };
        const processArray = (arr) =>
          Array.isArray(arr) ? arr.map((item) => tryDecodeBase64(item)) : [];
        setErrorDetails({
          message: errorData.message || "Invalid file format",
          missing_columns: processArray(errorData.missing_columns || []),
          expected_columns: processArray(errorData.expected_columns || []),
          extra_columns: processArray(errorData.extra_columns || []),
          found_columns: processArray(errorData.found_columns || []),
        });
        setShowErrorModal(true);
      } else {
        handleToastOpen(
          "error",
          error.message || "Upload failed. Please try again.",
        );
      }
      setIsUploading(false);
    }
  };

  const callProcessWithRetry = async (
    toolToUse,
    processData,
    modelToUse,
    totalCount,
    processingBatchIdToUse,
    maxRetries = 4,
  ) => {
    const getRetryDelay = (attempt) =>
      Math.min(3000 * Math.pow(2, attempt - 1), 20000);

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(
          `[callProcessWithRetry] Attempt ${attempt}/${maxRetries} — tool: ${toolToUse}`,
        );

        // window.enrichmentProgressCallback = (progress) => {
        //   console.log("Frontend Callback:", progress);

        //   setUploadProgress(Math.floor(progress.progress_percentage));

        //   setProcessingStats((prev) => ({
        //     ...prev,
        //     processed: progress.end_record,
        //     total: progress.total_records_in_batch ?? totalCount,
        //     chunkInfo: `Processing ${progress.start_record}-${progress.end_record}`,
        //   }));
        // };

        // window.enrichmentProgressCallback = (progress) => {
        //   // const pct = Math.floor(progress.progress_percentage ?? 0);
        //   const pct = Math.min(
        //     99,
        //     Math.floor(progress.progress_percentage ?? 0),
        //   );
        //   setUploadProgress(pct);
        //   setProcessingProgress(pct);

        //   const elapsedMs =
        //     Date.now() - (processingStartTimeRef.current || Date.now());
        //   const elapsedSec = Math.floor(elapsedMs / 1000);
        //   const elapsedMin = Math.floor(elapsedSec / 60);
        //   const elapsedSecs = elapsedSec % 60;

        //   // derive an estimated total from end_record / percentage since
        //   // total_records in this payload is not a usable number
        //   const estimatedTotal =
        //     progress.progress_percentage > 0
        //       ? Math.round(
        //           (progress.end_record / progress.progress_percentage) * 100,
        //         )
        //       : totalCount;

        //   const remainingRecords = Math.max(
        //     0,
        //     estimatedTotal - progress.end_record,
        //   );
        //   const avgMsPerRecord =
        //     progress.end_record > 0 ? elapsedMs / progress.end_record : 0;
        //   const remainingSec = progress.has_more
        //     ? Math.ceil((avgMsPerRecord * remainingRecords) / 1000)
        //     : 0;

        //   remainingSecondsRef.current = remainingSec; // keep the shared ticking effect in sync

        //   const remMin = Math.floor(remainingSec / 60);
        //   const remSecs = remainingSec % 60;

        //   setProcessingStats((prev) => ({
        //     ...prev,
        //     processed: progress.end_record,
        //     total: estimatedTotal,
        //     timeElapsed: `${elapsedMin}:${elapsedSecs < 10 ? "0" + elapsedSecs : elapsedSecs}`,
        //     timeRemaining: progress.has_more
        //       ? `${remMin}:${remSecs < 10 ? "0" + remSecs : remSecs}`
        //       : "Verifying...",
        //     chunkInfo: `Processing ${progress.start_record}-${progress.end_record} (${progress.processed_records} in this chunk)`,
        //   }));
        // };
        window.enrichmentProgressCallback = (progress) => {
          if (!progress.has_more) {
            isVerifyingRef.current = true; // stop the ticking timer from touching timeRemaining
          }

          const rawPct = Math.floor(progress.progress_percentage ?? 0);
          const pct = progress.has_more ? rawPct : 99;
          setUploadProgress(pct);
          setProcessingProgress(pct);

          const elapsedMs =
            Date.now() - (processingStartTimeRef.current || Date.now());
          const elapsedSec = Math.floor(elapsedMs / 1000);
          const elapsedMin = Math.floor(elapsedSec / 60);
          const elapsedSecs = elapsedSec % 60;

          const estimatedTotal =
            progress.progress_percentage > 0
              ? Math.round(
                  (progress.end_record / progress.progress_percentage) * 100,
                )
              : totalCount;

          const remainingRecords = Math.max(
            0,
            estimatedTotal - progress.end_record,
          );
          const avgMsPerRecord =
            progress.end_record > 0 ? elapsedMs / progress.end_record : 0;
          const remainingSec = progress.has_more
            ? Math.ceil((avgMsPerRecord * remainingRecords) / 1000)
            : 0;

          remainingSecondsRef.current = remainingSec;

          const remMin = Math.floor(remainingSec / 60);
          const remSecs = remainingSec % 60;

          setProcessingStats((prev) => ({
            ...prev,
            processed: progress.end_record,
            total: estimatedTotal,
            timeElapsed: `${elapsedMin}:${elapsedSecs < 10 ? "0" + elapsedSecs : elapsedSecs}`,
            timeRemaining: progress.has_more
              ? `${remMin}:${remSecs < 10 ? "0" + remSecs : remSecs}`
              : "Verifying...",
            chunkInfo: progress.has_more
              ? `Processing ${progress.start_record}-${progress.end_record} (${progress.processed_records} in this chunk)`
              : "Finalizing — verifying record counts...",
          }));
        };

        window.duplicateCheckProgressCallback = (progress) => {
          console.log("Duplicate Check Progress:", progress);

          if (!progress.has_more) {
            isVerifyingRef.current = true;
          }

          const rawPct = Math.floor(progress.progress_percentage ?? 0);
          const pct = progress.has_more ? rawPct : 99;
          setUploadProgress(pct);
          setProcessingProgress(pct);

          const elapsedMs =
            Date.now() - (processingStartTimeRef.current || Date.now());
          const elapsedSec = Math.floor(elapsedMs / 1000);
          const elapsedMin = Math.floor(elapsedSec / 60);
          const elapsedSecs = elapsedSec % 60;

          const remainingRecords = Math.max(
            0,
            (progress.total_records || 0) - (progress.end_record || 0),
          );
          const avgMsPerRecord =
            progress.end_record > 0 ? elapsedMs / progress.end_record : 0;
          const remainingSec = progress.has_more
            ? Math.ceil((avgMsPerRecord * remainingRecords) / 1000)
            : 0;

          remainingSecondsRef.current = remainingSec;
          const remMin = Math.floor(remainingSec / 60);
          const remSecs = remainingSec % 60;

          setProcessingStats((prev) => ({
            ...prev,
            processed: progress.end_record,
            total: progress.total_records ?? prev.total,
            timeElapsed: `${elapsedMin}:${elapsedSecs < 10 ? "0" + elapsedSecs : elapsedSecs}`,
            timeRemaining: progress.has_more
              ? `${remMin}:${remSecs < 10 ? "0" + remSecs : remSecs}`
              : "Verifying...",
            chunkInfo: progress.has_more
              ? `Processed ${progress.start_record}-${progress.end_record} of ${progress.total_records} (${progress.processed_records} in this chunk, ${progress.duplicates_in_chunk ?? 0} duplicates)`
              : "Finalizing — verifying record counts...",
          }));
        };

        const response = await processAutomationData(
          toolToUse,
          processData,
          modelToUse,
          totalCount,
          processingBatchIdToUse,
        );
        console.log(`[callProcessWithRetry] Attempt ${attempt} succeeded`);
        return response;
      } catch (err) {
        const status = err?.response?.status;
        const isRetryable =
          status === 504 ||
          status === 502 ||
          status === 503 ||
          err?.code === "ECONNABORTED" ||
          err?.message?.includes("timeout") ||
          err?.message?.includes("504") ||
          err?.message?.includes("502") ||
          err?.message?.includes("503") ||
          err?.message?.includes("Gateway") ||
          err?.message?.includes("network");

        console.warn(
          `[callProcessWithRetry] Attempt ${attempt} failed — status: ${status} | retryable: ${isRetryable} | msg: ${err?.message}`,
        );

        if (!isRetryable || attempt === maxRetries) {
          console.error(
            `[callProcessWithRetry] Giving up after ${attempt} attempt(s)`,
          );
          throw err;
        }

        const delay = getRetryDelay(attempt);
        console.log(`[callProcessWithRetry] Retrying in ${delay}ms...`);
        setProcessingStats((prev) => ({
          ...prev,
          chunkInfo: `Server busy (504) — retrying in ${Math.round(delay / 1000)}s... (attempt ${attempt}/${maxRetries})`,
        }));
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  };

  // ─── Poll /input_check until backend confirms completion ─────────────────────
  const pollProcessingStatus = async (processData, toolToUse, modelToUse) => {
    const maxAttempts = 120;
    const intervalMs = 5000;
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        console.log(
          `[pollProcessingStatus] Poll attempt ${attempt + 1}/${maxAttempts}`,
        );

        const fileModelResponse = await checkCountData(
          processData,
          toolToUse,
          modelToUse,
        );
        consecutiveErrors = 0;

        console.log(
          `[pollProcessingStatus] Status received: "${fileModelResponse.status}"`,
        );

        if (
          fileModelResponse.status === "completed" ||
          fileModelResponse.status === "start enrichment"
        ) {
          return fileModelResponse;
        }

        setProcessingStats((prev) => ({
          ...prev,
          chunkInfo: `Processing on server... (check ${attempt + 1}/${maxAttempts})`,
        }));
      } catch (pollErr) {
        consecutiveErrors++;
        console.warn(
          `[pollProcessingStatus] Poll attempt ${attempt + 1} failed (${consecutiveErrors} consecutive errors): ${pollErr.message}`,
        );

        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          throw new Error(
            `Status check failed ${MAX_CONSECUTIVE_ERRORS} times in a row: ${pollErr.message}`,
          );
        }
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error("Processing status check timeout after 10 minutes");
  };

  // ─── Classification: single chunk via streaming /process_records ───────────
  const processClassificationChunk = async (processData, limit, offset) => {
    const response = await fetch(
      // `https://devai.conasservices.com/v1/classify/classification/process_records`,   //for local testing
      `https://devai.conasservices.com/v1/uat_classify/classification/process_records`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key":
            localStorage.getItem("token") || sessionStorage.getItem("token"),
        },
        body: JSON.stringify({
          ...processData,
          data: processData?.data || [],
          limit,
          offset,
        }),
      },
    );

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(
        `process_records failed: ${response.status} — ${errText}`,
      );
    }

    // Backend now sends exactly ONE JSON object per chunk call (success or
    // error) instead of multiple NDJSON lines, so we just parse the body
    // directly — no more manual stream reading / line splitting needed.
    const result = await response.json();

    if (result?.status === "error") {
      throw new Error(result.detail || "Processing error from server");
    }

    return result; // records_processed, total_records_in_file, is_final_chunk, match_statistics, etc.
  };

  // ─── Classification: chunked processing loop for /process_records ──────────
  // Mirrors processE2EWithChunking / processManualUploadWithChunking, but drives
  // limit/offset windows against /process_records instead of /process_e2e.
  const processClassificationWithChunking = async (
    processData,
    modelToUse,
    totalRecords,
    processingBatchIdToUse,
  ) => {
    if (!totalRecords || totalRecords === 0) {
      handleToastOpen("warning", "No records found to process");
      throw new Error("No records to process");
    }

    const CHUNK_SIZE = 50; // backend hard-caps (offset - limit) at 50
    const MAX_RETRIES = 1;
    const RETRY_DELAY = 2000;
    const SAFETY_MAX_CHUNKS = Math.ceil(totalRecords / CHUNK_SIZE) + 10;

    let limit = 0;
    let knownTotal = totalRecords; // refined once backend reports total_records_in_file
    let cumulativeProcessed = 0;
    let successfulChunks = 0;
    let failedChunks = 0;
    let chunkIndex = 0;
    let isFinalChunk = false;
    const matchTotals = {
      taxonomy_exact: 0,
      model_new: 0,
      model_permutation: 0,
      no_match: 0,
    };
    const startTime = Date.now();

    setProcessingProgress(0);
    setProcessingStats({
      processed: 0,
      total: knownTotal,
      timeElapsed: "0:00",
      timeRemaining: "calculating...",
      chunkInfo: `Starting... (chunk 1/${Math.ceil(knownTotal / CHUNK_SIZE)})`,
    });

    while (!isFinalChunk) {
      if (chunkIndex >= SAFETY_MAX_CHUNKS) {
        throw new Error(
          "Chunked processing exceeded expected chunk count — aborting.",
        );
      }

      const offset = Math.min(limit + CHUNK_SIZE, knownTotal);
      let chunkSucceeded = false;
      let lastChunkErrorMessage = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const chunkResult = await processClassificationChunk(
            processData,
            limit,
            offset,
          );
          if (chunkResult?.status === "completed") {
            chunkSucceeded = true;
            successfulChunks++;

            knownTotal = chunkResult.total_records_in_file || knownTotal;
            isFinalChunk = Boolean(chunkResult.is_final_chunk);
            cumulativeProcessed +=
              chunkResult.records_processed || offset - limit;

            const stats = chunkResult.match_statistics || {};
            matchTotals.taxonomy_exact = stats.taxonomy_exact || 0;
            matchTotals.model_new = stats.model_new || 0;
            matchTotals.model_permutation = stats.model_permutation || 0;
            matchTotals.no_match = stats.no_match || 0;

            const elapsedMs = Date.now() - startTime;
            const elapsedMin = Math.floor(elapsedMs / 60000);
            const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);
            const timeElapsed = `${elapsedMin}:${elapsedSec < 10 ? "0" + elapsedSec : elapsedSec}`;

            let timeRemaining = "0:00";
            if (
              !isFinalChunk &&
              cumulativeProcessed > 0 &&
              cumulativeProcessed < knownTotal
            ) {
              const avgMs = elapsedMs / cumulativeProcessed;
              const remMs = avgMs * (knownTotal - cumulativeProcessed);
              const remMin = Math.floor(remMs / 60000);
              const remSec = Math.floor((remMs % 60000) / 1000);
              timeRemaining = `${remMin}:${remSec < 10 ? "0" + remSec : remSec}`;
              remainingSecondsRef.current = Math.ceil(remMs / 1000); // ← add this
            } else {
              remainingSecondsRef.current = 0; // ← and this, for the final chunk
            }

            const progress = Math.max(
              1,
              Math.min(
                99,
                Math.floor((cumulativeProcessed / knownTotal) * 100),
              ),
            );
            if (isFinalChunk) {
              isVerifyingRef.current = true; // ← stop the ticking timer from touching timeRemaining
            }

            setProcessingProgress(isFinalChunk ? 99 : progress);
            setProcessingStats({
              processed: cumulativeProcessed,
              total: knownTotal,
              timeElapsed,
              timeRemaining: isFinalChunk ? "Verifying..." : timeRemaining,
              // chunkInfo: `Chunk ${chunkIndex + 1} done — rows ${limit}-${offset} (${chunkResult.records_processed ?? 0} processed)`,
              chunkInfo: isFinalChunk
                ? "Finalizing — verifying record counts..."
                : `Chunk ${chunkIndex + 1} done — rows ${limit}-${offset} (${chunkResult.records_processed ?? 0} processed)`,
              matchRate: undefined,
              processingSpeed: undefined,
            });

            break;
          } else {
            lastChunkErrorMessage = `Unexpected status: ${chunkResult?.status}`;
            console.warn(
              `Classification chunk ${chunkIndex + 1} attempt ${attempt} — unexpected status:`,
              chunkResult?.status,
              chunkResult,
            );
          }
        } catch (err) {
          lastChunkErrorMessage = err.message;
          const is409 =
            err?.message?.includes("409") ||
            err?.message?.includes("already") ||
            err?.message?.includes("Conflict");

          if (is409) {
            chunkSucceeded = true;
            successfulChunks++;
            cumulativeProcessed = offset;
            break;
          }

          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY * attempt));
          } else {
            console.warn(
              `Classification chunk ${chunkIndex + 1} failed:`,
              err.message,
            );
          }
        }
      }

      if (!chunkSucceeded) {
        // ── STOP IMMEDIATELY: do not skip this chunk and move on ───────────────
        console.error(
          `[Classification] Chunk ${chunkIndex + 1} failed (records ${limit}–${offset}):`,
          lastChunkErrorMessage,
        );

        const errorMessage = `Processing failed at rows ${limit + 1}–${offset}. Please check those rows in your file and try again.`;

        setProcessingStats((prev) => ({
          ...prev,
          chunkInfo: `Stopped at rows ${limit + 1}–${offset}.`,
        }));

        const dataIssueError = new Error(errorMessage);
        dataIssueError.isDataIssue = true;
        throw dataIssueError;
      }

      limit = offset;
      chunkIndex++;
      if (limit >= knownTotal) {
        isFinalChunk = true;
      }

      if (!isFinalChunk) {
        await new Promise((r) => setTimeout(r, 200));
      }
    }

    // ── Finalize: same input_check call used by the other chunked flows ───────
    try {
      const checkPayload = { data: [{ folderId: processingBatchIdToUse }] };
      await checkCountData(checkPayload, "Classification", modelToUse);
    } catch (err) {
      console.warn("input_check failed (non-blocking):", err.message);
    }

    const finalElapsedMs = Date.now() - startTime;
    const finalMin = Math.floor(finalElapsedMs / 60000);
    const finalSec = Math.floor((finalElapsedMs % 60000) / 1000);

    setProcessingProgress(100);
    setProcessingStats({
      processed: cumulativeProcessed,
      total: knownTotal,
      timeElapsed: `${finalMin}:${finalSec < 10 ? "0" + finalSec : finalSec}`,
      timeRemaining: "0:00",
      chunkInfo: "All chunks complete",
    });

    // If we reach this point, every chunk above succeeded — any chunk that
    // failed now throws immediately (see above), so there's no "partial
    // failure" case left to report here.

    return {
      status: "completed",
      records_processed: cumulativeProcessed,
      total_records: knownTotal,
      match_statistics: matchTotals,
    };
  };
  // ─── Data Matching: single chunk via /process_records ──────────────────────
  const processDataMatchingChunk = async (payload) => {
    const response = await fetch(
      // ADJUST to your actual matching service base path — mirrors the
      // classification one but on the matching module

      `https://devai.conasservices.com/v1/data_match/match_activity/process_records`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key":
            localStorage.getItem("token") || sessionStorage.getItem("token"),
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(
        `process_records failed: ${response.status} — ${errText}`,
      );
    }

    // Backend yields exactly ONE JSON object per chunk call (same as
    // classification's process_records — no NDJSON line splitting needed).
    const result = await response.json();

    if (result?.status === "error") {
      throw new Error(
        result.detail || result.message || "Processing error from server",
      );
    }

    return result; // records_processed, total_records_in_batch, has_more, etc.
  };

  // ─── Data Matching: chunked processing loop for /process_records ───────────
  // Mirrors processClassificationWithChunking, but driven by has_more /
  // total_records_in_batch (the fields process_records actually returns)
  // instead of is_final_chunk / total_records_in_file.
  const processDataMatchingWithChunking = async (
    modelToUse,
    totalRecords,
    processingBatchIdToUse,
    referenceBatchId, // pass this for "Custom Model" uploads, omit for ICTSI-GT00 / ICTSI-All Terminals / GCT
  ) => {
    if (!totalRecords || totalRecords === 0) {
      handleToastOpen("warning", "No records found to process");
      throw new Error("No records to process");
    }

    const CHUNK_SIZE = 50; // backend hard-caps (offset - limit) at 50 for /process_records
    const MAX_RETRIES = 1;
    const RETRY_DELAY = 2000;
    const SAFETY_MAX_CHUNKS = Math.ceil(totalRecords / CHUNK_SIZE) + 10;

    let limit = 0;
    let knownTotal = totalRecords; // refined once backend reports total_records_in_batch
    let cumulativeProcessed = 0;
    let successfulChunks = 0;
    let failedChunks = 0;
    let chunkIndex = 0;
    let hasMore = true;
    const startTime = Date.now();

    setProcessingProgress(0);
    setProcessingStats({
      processed: 0,
      total: knownTotal,
      timeElapsed: "0:00",
      timeRemaining: "calculating...",
      chunkInfo: `Starting... (chunk 1/${Math.ceil(knownTotal / CHUNK_SIZE)})`,
    });

    while (hasMore) {
      if (chunkIndex >= SAFETY_MAX_CHUNKS) {
        throw new Error(
          "Chunked processing exceeded expected chunk count — aborting.",
        );
      }

      const offset = Math.min(limit + CHUNK_SIZE, knownTotal);
      let chunkSucceeded = false;
      let lastChunkErrorMessage = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const payload = {
            batch_id: processingBatchIdToUse,
            limit,
            offset,
            ...(referenceBatchId
              ? { reference_batch_id: referenceBatchId }
              : { model_name: modelToUse }),
          };

          const chunkResult = await processDataMatchingChunk(payload);

          if (chunkResult?.status === "completed") {
            chunkSucceeded = true;
            successfulChunks++;

            knownTotal = chunkResult.total_records_in_batch || knownTotal;
            hasMore = Boolean(chunkResult.has_more);
            cumulativeProcessed +=
              chunkResult.records_processed || offset - limit;

            const elapsedMs = Date.now() - startTime;
            const elapsedMin = Math.floor(elapsedMs / 60000);
            const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);
            const timeElapsed = `${elapsedMin}:${elapsedSec < 10 ? "0" + elapsedSec : elapsedSec}`;

            let timeRemaining = "0:00";
            if (
              hasMore &&
              cumulativeProcessed > 0 &&
              cumulativeProcessed < knownTotal
            ) {
              const avgMs = elapsedMs / cumulativeProcessed;
              const remMs = avgMs * (knownTotal - cumulativeProcessed);
              const remMin = Math.floor(remMs / 60000);
              const remSec = Math.floor((remMs % 60000) / 1000);
              timeRemaining = `${remMin}:${remSec < 10 ? "0" + remSec : remSec}`;
              remainingSecondsRef.current = Math.ceil(remMs / 1000); // ← add this
            } else {
              remainingSecondsRef.current = 0; // ← add this
            }

            const progress = Math.max(
              1,
              Math.min(
                99,
                Math.floor((cumulativeProcessed / knownTotal) * 100),
              ),
            );
            if (!hasMore) {
              isVerifyingRef.current = true;
            }
            setProcessingProgress(hasMore ? progress : 99);
            setProcessingStats({
              processed: cumulativeProcessed,
              total: knownTotal,
              timeElapsed,
              timeRemaining: hasMore ? timeRemaining : "Verifying...",
              // chunkInfo: `Chunk ${chunkIndex + 1} done — rows ${limit}-${offset} (${chunkResult.records_processed ?? 0} processed)`,
              chunkInfo: hasMore
                ? `Chunk ${chunkIndex + 1} done — rows ${limit}-${offset} (${chunkResult.records_processed ?? 0} processed)`
                : "Finalizing — verifying record counts...",
            });

            break;
          } else if (
            chunkResult?.status === "ok" ||
            chunkResult?.status === "success" ||
            chunkResult?.status === "processed" || // some other non-standard success status
            chunkResult?.status === 200 ||
            chunkResult?.records_processed > 0
          ) {
            // server did the work but replied with a different success status
            chunkSucceeded = true;
            successfulChunks++;
            cumulativeProcessed +=
              chunkResult.records_processed || offset - limit;
            knownTotal = chunkResult.total_records_in_batch || knownTotal;
            hasMore = Boolean(chunkResult.has_more);
            if (offset >= knownTotal) hasMore = false;

            const elapsedMs = Date.now() - startTime;
            const elapsedMin = Math.floor(elapsedMs / 60000);
            const elapsedSec = Math.floor((elapsedMs % 60000) / 1000);

            setProcessingProgress(
              hasMore
                ? Math.max(
                    1,
                    Math.min(
                      99,
                      Math.floor((cumulativeProcessed / knownTotal) * 100),
                    ),
                  )
                : 100,
            );
            if (!hasMore) {
              isVerifyingRef.current = true;
            }
            setProcessingStats({
              processed: cumulativeProcessed,
              total: knownTotal,
              timeElapsed: `${elapsedMin}:${elapsedSec < 10 ? "0" + elapsedSec : elapsedSec}`,
              //
              timeRemaining: hasMore ? "calculating..." : "Verifying...",
              chunkInfo: hasMore
                ? `Chunk ${chunkIndex + 1} done (non-standard status: ${chunkResult?.status})`
                : "Finalizing — verifying record counts...",
            });
            break;
          } else {
            // truly unknown response — log it, let retry loop try again
            // chunkSucceeded stays false, so the outer retry loop will try again
            lastChunkErrorMessage = `Unexpected status: ${chunkResult?.status}`;
            console.warn(
              `Data Matching chunk ${chunkIndex + 1} attempt ${attempt} — unexpected status:`,
              chunkResult?.status,
              chunkResult,
            );
          }
        } catch (err) {
          lastChunkErrorMessage = err.message;

          const is409 =
            err?.message?.includes("409") ||
            err?.message?.includes("already") ||
            err?.message?.includes("Conflict");

          if (is409) {
            chunkSucceeded = true;
            successfulChunks++;
            cumulativeProcessed = offset;
            break;
          }

          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY * attempt));
          } else {
            console.warn(
              `Data Matching chunk ${chunkIndex + 1} failed:`,
              err.message,
            );
          }
        }
      }

      if (!chunkSucceeded) {
        console.error(
          `[DataMatching] Chunk ${chunkIndex + 1} failed (records ${limit}–${offset}):`,
          lastChunkErrorMessage,
        );

        const errorMessage = `Processing failed at rows ${limit + 1}–${offset}. Please check those rows in your file and try again.`;

        setProcessingStats((prev) => ({
          ...prev,
          chunkInfo: `Stopped at rows ${limit + 1}–${offset}.`,
        }));

        const dataIssueError = new Error(errorMessage);
        dataIssueError.isDataIssue = true;
        throw dataIssueError;
      }

      limit = offset;
      chunkIndex++;
      if (limit >= knownTotal) hasMore = false;

      if (hasMore) {
        await new Promise((r) => setTimeout(r, 500));
      }
    }

    // ── Finalize: same input_check call the other chunked flows use ───────────
    try {
      const checkPayload = { data: [{ folderId: processingBatchIdToUse }] };
      await checkCountData(checkPayload, "Data Matching", modelToUse);
    } catch (err) {
      console.warn("input_check failed (non-blocking):", err.message);
    }

    const finalElapsedMs = Date.now() - startTime;
    const finalMin = Math.floor(finalElapsedMs / 60000);
    const finalSec = Math.floor((finalElapsedMs % 60000) / 1000);

    setProcessingProgress(100);
    isVerifyingRef.current = false;
    setProcessingStats({
      processed: cumulativeProcessed,
      total: knownTotal,
      timeElapsed: `${finalMin}:${finalSec < 10 ? "0" + finalSec : finalSec}`,
      timeRemaining: "0:00",
      chunkInfo: "All chunks complete",
    });

    // If we reach this point, every chunk above succeeded — any chunk that
    // exhausted its retries now throws immediately (see above), so there's no
    // "partial failure" case left to report here.

    return {
      status: "completed",
      records_processed: cumulativeProcessed,
      total_records: knownTotal,
    };
  };

  // ─── MAIN: handle Start Processing button (manual upload flow) ───────────────
  const handleStartProcessing = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const urlBatchId = params.get("batchId");
      const urlToolName = params.get("toolName");
      const urlModelName = params.get("modelName");
      const urlTotalRecords = params.get("totalRecords");
      const urlToBeProcessed = params.get("toBeProcessed") === "true";

      clearAllIntervals();

      const startTime = Date.now();
      processingStartTimeRef.current = startTime;
      isVerifyingRef.current = false;
      setIsProcessing(true);
      setProcessingProgress(0);
      console.log("18");
      setProcessingStats({
        processed: 0,
        total:
          totalCount || (urlTotalRecords ? parseInt(urlTotalRecords, 10) : 0),
        timeElapsed: "0:00",
        timeRemaining: "calculating...",
      });

      const currentTool = selectedTool || urlToolName;
      const recordCount =
        totalCount || (urlTotalRecords ? parseInt(urlTotalRecords, 10) : 0);

      // ── MPN Segregation progress tracking setup ──────────────────────────────
      if (currentTool === "Mpn Segregation") {
        const totalChunks = Math.ceil(recordCount / 200);
        const progressTracking = {
          actualRecordsProcessed: 0,
          completedChunks: 0,
          startTime,
        };
        window.mpnProgressCallback = (batchResponse) => {
          const chunkIndex = batchResponse.chunk_index;
          const recordsProcessed = batchResponse.records_processed || 0;

          progressTracking.hasReceivedFirstBatch = true;
          progressTracking.actualRecordsProcessed += recordsProcessed;
          progressTracking.completedChunks = chunkIndex + 1;
          progressTracking.lastUpdateTime = Date.now();

          if (progressTracking.actualRecordsProcessed > recordCount) {
            progressTracking.actualRecordsProcessed = recordCount;
          }

          const allChunksDone =
            progressTracking.actualRecordsProcessed >= recordCount;
          if (allChunksDone) {
            isVerifyingRef.current = true;
          }

          const exactProgress = Math.min(
            99,
            Math.floor(
              (progressTracking.actualRecordsProcessed / recordCount) * 100,
            ),
          );

          setProcessingProgress(exactProgress);

          // -------------------------------
          // Time calculations
          // -------------------------------
          const elapsedMs = Date.now() - startTime;
          const elapsedSeconds = elapsedMs / 1000;

          const elapsedMinutes = Math.floor(elapsedSeconds / 60);
          const elapsedSecs = Math.floor(elapsedSeconds % 60);

          const speed = parseFloat(
            batchResponse.extraction_stats?.overall_speed || "0",
          );

          const averageSpeed =
            progressTracking.actualRecordsProcessed / elapsedSeconds;

          const remainingRecords =
            recordCount - progressTracking.actualRecordsProcessed;

          const remainingSeconds = allChunksDone
            ? 0
            : remainingRecords / averageSpeed;

          remainingSecondsRef.current = allChunksDone
            ? 0
            : Math.ceil(remainingSeconds);
          const remainingMinutes = Math.floor(remainingSeconds / 60);
          const remainingSecs = Math.floor(remainingSeconds % 60);

          // setRemainingSeconds(Math.ceil(remainingSeconds));
          setRemainingSeconds(allChunksDone ? 0 : Math.ceil(remainingSeconds));

          setProcessingStats({
            processed: progressTracking.actualRecordsProcessed,
            total: recordCount,

            timeElapsed: `${elapsedMinutes}:${
              elapsedSecs < 10 ? "0" + elapsedSecs : elapsedSecs
            }`,

            timeRemaining: allChunksDone
              ? "Verifying..."
              : `${remainingMinutes}:${
                  remainingSecs < 10 ? "0" + remainingSecs : remainingSecs
                }`,

            // chunkInfo: `Chunk ${chunkIndex + 1}/${totalChunks} completed (${recordsProcessed} records)`,
            chunkInfo: allChunksDone
              ? "Finalizing — verifying record counts..."
              : `Chunk ${chunkIndex + 1}/${totalChunks} completed (${recordsProcessed} records)`,
            matchRate: batchResponse.extraction_stats?.match_rate,

            processingSpeed: `${speed.toFixed(1)} rec/sec`,
          });
        };
      }

      // ── Timeout durations ────────────────────────────────────────────────────
      let timeoutDuration;
      if (currentTool === "Enrichment") {
        timeoutDuration =
          recordCount <= 500
            ? 30 * 1000
            : recordCount <= 2500
              ? 90 * 1000
              : 2 * 60 * 1000;
      } else if (currentTool === "Duplicate Check") {
        timeoutDuration = 3 * 30 * 1000;
      } else if (currentTool === "Classification") {
        if (recordCount <= 500) timeoutDuration = 3 * 60 * 1000;
        else if (recordCount <= 1000) timeoutDuration = 6 * 60 * 1000;
        else if (recordCount <= 2000) timeoutDuration = 9 * 60 * 1000;
        else if (recordCount <= 3000) timeoutDuration = 12 * 60 * 1000;
        else if (recordCount <= 4000) timeoutDuration = 14 * 60 * 1000;
        else timeoutDuration = 15 * 60 * 1000;
      } else if (currentTool === "Data Matching") {
        const modelToUse = urlToBeProcessed
          ? urlModelName
          : modelSelectionType === "existing"
            ? selectedModel
            : uploadedModelFile?.name || "Custom Model";
        if (
          modelToUse === "ICTSI-GT00" ||
          modelToUse === "ICTSI-All Terminals"
        ) {
          timeoutDuration =
            recordCount <= 500
              ? 5 * 60 * 1000
              : recordCount <= 1000
                ? 10 * 60 * 1000
                : recordCount <= 5000
                  ? 18 * 60 * 1000
                  : recordCount <= 10000
                    ? 28 * 60 * 1000
                    : 40 * 60 * 1000;
        } else if (modelToUse === "GCT") {
          timeoutDuration =
            recordCount <= 500
              ? 3 * 60 * 1000
              : recordCount <= 1000
                ? 5 * 60 * 1000
                : recordCount <= 5000
                  ? 8 * 60 * 1000
                  : recordCount <= 10000
                    ? 12 * 60 * 1000
                    : 20 * 60 * 1000;
        } else {
          timeoutDuration =
            recordCount <= 500
              ? 5 * 60 * 1000
              : recordCount <= 1000
                ? 10 * 60 * 1000
                : recordCount <= 5000
                  ? 18 * 60 * 1000
                  : recordCount <= 10000
                    ? 28 * 60 * 1000
                    : 40 * 60 * 1000;
        }
      } else {
        timeoutDuration = 30 * 60 * 1000;
      }

      // ── Fake progress interval — only for non-Classification, non-MPN, non-DataMatching tools ──
      if (
        currentTool !== "Mpn Segregation" &&
        currentTool !== "Classification" &&
        currentTool !== "Data Matching" &&
        currentTool !== "Enrichment" &&
        currentTool !== "Duplicate Check"
      ) {
        const progressInterval = setInterval(() => {
          const elapsedMs = Date.now() - startTime;
          const adjustedElapsedMs = Math.max(0, elapsedMs - 8000);
          const elapsedMinutes = Math.floor(adjustedElapsedMs / 60000);
          const elapsedSeconds = Math.floor((adjustedElapsedMs % 60000) / 1000);
          const currentProgress = Math.min(
            90,
            Math.floor((elapsedMs / timeoutDuration) * 100),
          );
          setProcessingProgress((prevProgress) =>
            Math.max(prevProgress, currentProgress),
          );
          const estimatedProcessed = Math.floor(
            (recordCount * currentProgress) / 100,
          );

          remainingSecondsRef.current = Math.max(
            0,
            remainingSecondsRef.current - 1,
          );
          const remainingMs = Math.max(0, timeoutDuration - elapsedMs) + 8000;
          const remainingMinutes = Math.floor(remainingMs / 60000);
          const remainingSeconds = Math.floor((remainingMs % 60000) / 1000);
          setProcessingStats({
            processed: estimatedProcessed,
            total: recordCount,
            timeElapsed: `${elapsedMinutes}:${elapsedSeconds < 10 ? "0" + elapsedSeconds : elapsedSeconds}`,
            timeRemaining: `${remainingMinutes}:${remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds}`,
          });
        }, 1000);
        uploadIntervalRef.current = progressInterval;
      }

      let hasCompletedProcessing = false;
      let processResponse;
      let processData = data;

      try {
        // ── Reconstruct processData when coming from history "reprocess" link ──
        if (urlToBeProcessed && urlBatchId) {
          let baseFolderPath = "";
          if (urlToolName === "Data Matching")
            baseFolderPath = "matching-activity/";
          else if (
            urlToolName === "Mpn Segregation" ||
            urlToolName === "alternate Manufacturer"
          )
            baseFolderPath = "mpn-segregation/";
          else
            baseFolderPath = `${urlToolName.toLowerCase().replace(/\s+/g, "-")}/`;

          const mockFileName = `${urlToolName.replace(/\s+/g, "_")}_${urlModelName.replace(/\s+/g, "_")}.xlsx`;
          let uploadResults;
          if (
            urlToolName === "Mpn Segregation" ||
            urlToolName === "Classification"
          ) {
            uploadResults = [
              {
                originalFile: mockFileName,
                fileSize: 0,
                fileType:
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                s3Location: null,
                key: null,
                folderId: urlBatchId,
                activityType: urlToolName,
                databaseOnly: true,
              },
            ];
          } else {
            const key = `${baseFolderPath}${urlBatchId}/input_file.xlsx`;
            const s3Url = `https://${process.env.REACT_APP_UAT_AWS_BUCKET}.s3.amazonaws.com/${key}`;
            uploadResults = [
              {
                originalFile: mockFileName,
                fileSize: 0,
                fileType:
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                s3Location: s3Url,
                key,
                folderId: urlBatchId,
                activityType: urlToolName,
              },
            ];
          }
          processData = { status: 201, data: uploadResults };
          setData(processData);
        }

        const toolToUse = urlToBeProcessed ? urlToolName : selectedTool;
        let modelToUse;
        if (urlToBeProcessed) {
          modelToUse = urlModelName;
        } else {
          if (selectedTool === "Data Matching") modelToUse = selectedModel;
          else if (selectedTool === "Enrichment") modelToUse = selectedCustomer;
          else if (selectedTool === "Duplicate Check") modelToUse = "ICTSI";
          else modelToUse = selectedModel;
        }

        let modelFileInfo = null;
        if (selectedTool === "Data Matching" && modelSelectionType === "upload")
          modelFileInfo = uploadedModelS3Location;

        let processingBatchIdToUse;
        if (modelFileInfo) {
          processingBatchIdToUse = modelFileInfo.batch_id;
        } else {
          processingBatchIdToUse =
            processData?.data?.[0]?.folderId ||
            currentFolderId ||
            batchId ||
            urlBatchId;
        }

        setBatchId(processingBatchIdToUse);

        // ── CLASSIFICATION: chunked processing via /process_records ─────────────
        if (currentTool === "Classification") {
          clearAllIntervals();

          try {
            const chunkResult = await processClassificationWithChunking(
              processData,
              modelToUse,
              recordCount,
              processingBatchIdToUse,
            );

            if (chunkResult && chunkResult.status === "completed") {
              setProcessRecords(chunkResult.records_processed || recordCount);
              setProcessingProgress(100);
              isVerifyingRef.current = false;
              setProcessingStats((prev) => ({
                ...prev,
                processed: prev.total,
                timeRemaining: "0:00",
              }));
              setTimeout(() => {
                processingStartTimeRef.current = null;
                setIsProcessing(false);
                setIsProcessingComplete(true);
                setActiveStep(3);
                handleToastOpen(
                  "success",
                  `Processing complete! ${chunkResult.records_processed} records processed.`,
                );
              }, 500);
            } else {
              throw new Error(
                "Chunked processing ended without completed status",
              );
            }
          } catch (chunkErr) {
            if (chunkErr.isDataIssue) {
              // ── Genuine data problem — stop here, no fallback poll ────────────
              console.error(
                "Classification stopped due to a data issue:",
                chunkErr.message,
              );
              clearAllIntervals();
              setIsProcessing(false);
              handleToastOpen("error", chunkErr.message);
            } else {
              // ── Fallback: timer-based poll — unchanged from before ──────────────────
              console.warn(
                "Classification chunked processing failed, falling back to poll:",
                chunkErr.message,
              );

              clearAllIntervals();
              const fallbackProgressInterval = setInterval(() => {
                const elapsedMs = Date.now() - startTime;
                const elapsedMinutes = Math.floor(elapsedMs / 60000);
                const elapsedSeconds = Math.floor((elapsedMs % 60000) / 1000);
                const currentProgress = Math.min(
                  90,
                  Math.floor((elapsedMs / timeoutDuration) * 100),
                );
                setProcessingProgress((prev) =>
                  Math.max(prev, currentProgress),
                );
                const estimatedProcessed = Math.floor(
                  (recordCount * currentProgress) / 100,
                );
                const remainingMs =
                  Math.max(0, timeoutDuration - elapsedMs) + 8000;
                const remainingMinutes = Math.floor(remainingMs / 60000);
                const remainingSeconds = Math.floor(
                  (remainingMs % 60000) / 1000,
                );
                setProcessingStats({
                  processed: estimatedProcessed,
                  total: recordCount,
                  timeElapsed: `${elapsedMinutes}:${elapsedSeconds < 10 ? "0" + elapsedSeconds : elapsedSeconds}`,
                  timeRemaining: `${remainingMinutes}:${remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds}`,
                });
              }, 1000);
              uploadIntervalRef.current = fallbackProgressInterval;

              // Schedule the same poll checkCountData uses in the old VM
              const timerId = setTimeout(async () => {
                try {
                  const checkPayload = {
                    data: [{ folderId: processingBatchIdToUse }],
                  };
                  const fileModelResponse = await checkCountData(
                    checkPayload,
                    currentTool,
                    modelToUse,
                  );
                  clearAllIntervals();

                  if (
                    fileModelResponse.status === "completed" ||
                    fileModelResponse.status === "start enrichment"
                  ) {
                    setBatchId(
                      fileModelResponse.batch_id || processingBatchIdToUse,
                    );
                    setProcessRecords(fileModelResponse.records_processed);
                    setProcessingProgress(100);
                    setProcessingStats((prev) => ({
                      ...prev,
                      processed: prev.total,
                      timeRemaining: "0:00",
                    }));
                    setTimeout(() => {
                      setIsProcessing(false);
                      setIsProcessingComplete(true);
                      setActiveStep(3);
                      handleToastOpen("success", "Processing complete!");
                    }, 500);
                  } else {
                    setIsProcessing(false);
                    handleToastOpen(
                      "error",
                      fileModelResponse.message || "Processing failed",
                    );
                  }
                } catch (pollErr) {
                  clearAllIntervals();
                  setIsProcessing(false);
                  handleToastOpen(
                    "error",
                    pollErr.response?.data?.message ||
                      pollErr.message ||
                      "Error checking processing status",
                  );
                }
              }, timeoutDuration);

              setCheckCountTimer(timerId);
            }
          }
        } else if (currentTool === "Data Matching") {
          // ── DATA MATCHING: chunked processing via /process_records ────────────
          clearAllIntervals();

          try {
            const referenceBatchId =
              modelSelectionType === "upload"
                ? uploadedModelS3Location?.batch_id
                : null;

            const chunkResult = await processDataMatchingWithChunking(
              modelToUse,
              recordCount,
              processingBatchIdToUse,
              referenceBatchId,
            );

            if (chunkResult && chunkResult.status === "completed") {
              setProcessRecords(chunkResult.records_processed || recordCount);
              setProcessingProgress(100);
              setProcessingStats({
                processed: chunkResult.records_processed,
                total: chunkResult.total_records,
                timeElapsed: processingStats.timeElapsed || "0:00",
                timeRemaining: "0:00",
                chunkInfo: "All chunks complete",
              });
              setTimeout(() => {
                setIsProcessing(false);
                setIsProcessingComplete(true);
                setActiveStep(3);
                handleToastOpen(
                  "success",
                  `Processing complete! ${chunkResult.records_processed} records processed.`,
                );
              }, 500);
            } else {
              throw new Error(
                "Chunked processing ended without completed status",
              );
            }
          } catch (chunkErr) {
            if (chunkErr.isDataIssue) {
              // ── Genuine data problem — stop here, no fallback poll ────────────
              console.error(
                "Data Matching stopped due to a data issue:",
                chunkErr.message,
              );
              clearAllIntervals();
              setIsProcessing(false);
              handleToastOpen("error", chunkErr.message);
            } else {
              // ── Fallback: timer-based poll — mirrors Classification's fallback ──────
              console.warn(
                "Data Matching chunked processing failed, falling back to poll:",
                chunkErr.message,
              );

              const fallbackProgressInterval = setInterval(() => {
                const elapsedMs = Date.now() - startTime;
                const elapsedMinutes = Math.floor(elapsedMs / 60000);
                const elapsedSeconds = Math.floor((elapsedMs % 60000) / 1000);
                const currentProgress = Math.min(
                  90,
                  Math.floor((elapsedMs / timeoutDuration) * 100),
                );
                setProcessingProgress((prev) =>
                  Math.max(prev, currentProgress),
                );
                const estimatedProcessed = Math.floor(
                  (recordCount * currentProgress) / 100,
                );
                const remainingMs =
                  Math.max(0, timeoutDuration - elapsedMs) + 8000;
                const remainingMinutes = Math.floor(remainingMs / 60000);
                const remainingSeconds = Math.floor(
                  (remainingMs % 60000) / 1000,
                );
                setProcessingStats({
                  processed: estimatedProcessed,
                  total: recordCount,
                  timeElapsed: `${elapsedMinutes}:${elapsedSeconds < 10 ? "0" + elapsedSeconds : elapsedSeconds}`,
                  timeRemaining: `${remainingMinutes}:${remainingSeconds < 10 ? "0" + remainingSeconds : remainingSeconds}`,
                });
              }, 1000);
              uploadIntervalRef.current = fallbackProgressInterval;

              const timerId = setTimeout(async () => {
                try {
                  const checkPayload = {
                    data: [{ folderId: processingBatchIdToUse }],
                  };
                  const fileModelResponse = await checkCountData(
                    checkPayload,
                    currentTool,
                    modelToUse,
                  );
                  clearAllIntervals();

                  if (
                    fileModelResponse.status === "completed" ||
                    fileModelResponse.status === "start enrichment"
                  ) {
                    setBatchId(
                      fileModelResponse.batch_id || processingBatchIdToUse,
                    );
                    setProcessRecords(fileModelResponse.records_processed);
                    setProcessingProgress(100);
                    setProcessingStats((prev) => ({
                      ...prev,
                      processed: prev.total,
                      timeRemaining: "0:00",
                    }));
                    setTimeout(() => {
                      setIsProcessing(false);
                      setIsProcessingComplete(true);
                      setActiveStep(3);
                      handleToastOpen("success", "Processing complete!");
                    }, 500);
                  } else {
                    setIsProcessing(false);
                    handleToastOpen(
                      "error",
                      fileModelResponse.message || "Processing failed",
                    );
                  }
                } catch (pollErr) {
                  clearAllIntervals();
                  setIsProcessing(false);
                  handleToastOpen(
                    "error",
                    pollErr.response?.data?.message ||
                      pollErr.message ||
                      "Error checking processing status",
                  );
                }
              }, timeoutDuration);

              setCheckCountTimer(timerId);
            }
          }
        } else {
          // ── ALL OTHER TOOLS: original single-call flow with retry + poll ─────
          try {
            processResponse = await callProcessWithRetry(
              toolToUse,
              processData,
              modelToUse,
              totalCount,
              processingBatchIdToUse,
            );

            // ── MPN Segregation: handle streaming response ───────────────────
            
            // ── All other non-Classification tools: poll for completion ───────
            if (!hasCompletedProcessing) {
              const fileModelResponse = await pollProcessingStatus(
                processData,
                toolToUse,
                modelToUse,
              );

              clearAllIntervals();
              setBatchId(fileModelResponse.batch_id);
              setProcessRecords(fileModelResponse.records_processed);
              setProcessingProgress(100);
              isVerifyingRef.current = false;
              setProcessingStats((prevStats) => ({
                ...prevStats,
                processed: prevStats.total,
                timeRemaining: "0:00",
              }));

              if (toolToUse === "Enrichment") {
                handleViewHistory();
              } else {
                setIsProcessing(false);
                setIsProcessingComplete(true);
                setActiveStep(3);
                handleToastOpen("success", "Processing complete!");
              }
            }
          } catch (processError) {
            console.error("Processing error:", processError);
            if (currentTool === "Mpn Segregation") {
              if (window.mpnProgressCallback) delete window.mpnProgressCallback;
              if (window.mpnProgressInterval) {
                clearInterval(window.mpnProgressInterval);
                delete window.mpnProgressInterval;
              }
            }
            // Fallback timer for non-Classification, non-MPN, non-DataMatching tools
            if (!hasCompletedProcessing) {
              const timerId = setTimeout(async () => {
                if (hasCompletedProcessing) return;
                try {
                  const tu = urlToBeProcessed ? urlToolName : selectedTool;
                  let mu;
                  if (urlToBeProcessed) {
                    mu = urlModelName;
                  } else {
                    if (selectedTool === "Data Matching")
                      mu =
                        modelSelectionType === "existing"
                          ? selectedModel
                          : uploadedModelFile?.name || "Custom Model";
                    else if (selectedTool === "Enrichment")
                      mu = selectedCustomer;
                    else mu = selectedModel;
                  }
                  const fileModelResponse = await checkCountData(
                    processData,
                    tu,
                    mu,
                  );
                  clearAllIntervals();
                  if (
                    fileModelResponse.status === "completed" ||
                    fileModelResponse.status === "start enrichment"
                  ) {
                    setBatchId(fileModelResponse.batch_id);
                    setProcessRecords(fileModelResponse.records_processed);
                    const originalFileName =
                      processData.data?.[0]?.originalFile || "";
                    
                    setProcessingProgress(100);
                    setProcessingStats((prevStats) => ({
                      ...prevStats,
                      processed: prevStats.total,
                      timeRemaining: "0:00",
                    }));
                    setTimeout(() => {
                      if (selectedTool === "Enrichment") {
                        handleViewHistory();
                      } else {
                        setIsProcessing(false);
                        setIsProcessingComplete(true);
                        setActiveStep(3);
                        handleToastOpen("success", "Processing complete!");
                      }
                    }, 500);
                  } else {
                    setIsProcessing(false);
                    handleToastOpen(
                      "error",
                      fileModelResponse.message || "Processing failed",
                    );
                  }
                } catch (error) {
                  clearAllIntervals();
                  setIsProcessing(false);
                  handleToastOpen(
                    "error",
                    error.response?.data?.message ||
                      "Error checking count data",
                  );
                }
              }, timeoutDuration);
              setCheckCountTimer(timerId);
            }
          }
        }
      } catch (setupError) {
        console.error("Setup error:", setupError);
        clearAllIntervals();
        if (currentTool === "Mpn Segregation") {
          if (window.mpnProgressCallback) delete window.mpnProgressCallback;
          if (window.mpnProgressInterval) {
            clearInterval(window.mpnProgressInterval);
            delete window.mpnProgressInterval;
          }
        }
        setIsProcessing(false);
        handleToastOpen(
          "error",
          setupError.message || "Error setting up processing",
        );
      }

      setFileUploaded(false);
    } catch (error) {
      console.error("Processing error:", error);
      clearAllIntervals();
      if (window.mpnProgressCallback) delete window.mpnProgressCallback;
      if (window.mpnProgressInterval) {
        clearInterval(window.mpnProgressInterval);
        delete window.mpnProgressInterval;
      }
      setIsProcessing(false);
      handleToastOpen("error", error.message || "Error during processing");
    }
  };

  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  useEffect(() => {}, [remainingSeconds]);

  // const handleDownload = async () => {
  //   console.log(
  //     "handleDownload called, isProcessingComplete:",
  //     isProcessingComplete,
  //   );
  //   if (!isProcessingComplete) return;
  //   setCOpen(true);
  //   try {
  //     let modelForDownload;
  //     if (selectedTool === "Data Matching")
  //       modelForDownload =
  //         modelSelectionType === "existing"
  //           ? selectedModel
  //           : uploadedModelFile?.name || "Custom Model";
  //     else if (selectedTool === "Duplicate Check") modelForDownload = "ICTSI";
  //     else modelForDownload = selectedModel;

  //     const downloadData = await downloadProcessedData(
  //       selectedTool,
  //       batchId,
  //       modelForDownload,
  //     );
  //     const formatName = (name) => (name ? name.replace(/[\s-]+/g, "_") : "");
  //     const filename = `${formatName(selectedTool)}_${formatName(modelForDownload)}_${new Date()
  //       .toISOString()
  //       .replace(/[-:]/g, "")
  //       .slice(0, 15)
  //       .replace("T", "_")}.xlsx`;
  //     const binaryString = atob(downloadData.base64Data);
  //     const bytes = new Uint8Array(binaryString.length);
  //     for (let i = 0; i < binaryString.length; i++)
  //       bytes[i] = binaryString.charCodeAt(i);
  //     const blob = new Blob([bytes], {
  //       type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     });
  //     const downloadUrl = URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = downloadUrl;
  //     link.download = filename;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
  //     handleToastOpen("success", "Excel File Downloaded Successfully");
  //   } catch (error) {
  //     if (error.name !== "AbortError")
  //       handleToastOpen(
  //         "error",
  //         "Excel File Download Failed: " + (error.message || "Unknown error"),
  //       );
  //   } finally {
  //     setCOpen(false);
  //     setTimeout(() => handleViewHistory(), 500);
  //   }
  // };

  const handleDownload = async () => {
    console.log(
      "handleDownload called, isProcessingComplete:",
      isProcessingComplete,
    );
    if (!isProcessingComplete) return;
    setCOpen(true);
    try {
      let modelForDownload;
      if (selectedTool === "Data Matching")
        modelForDownload =
          modelSelectionType === "existing"
            ? selectedModel
            : uploadedModelFile?.name || "Custom Model";
      else if (selectedTool === "Duplicate Check") modelForDownload = "ICTSI";
      else modelForDownload = selectedModel;

      if (selectedTool === "Data Matching") {
        // ═══ S3 presigned-URL flow (manual Data Matching only) ═══
        const result = await downloadProcessedDataViaS3(
          selectedTool,
          batchId,
          modelForDownload,
        );

        if (!result?.download_url) {
          throw new Error("Server did not return a download URL");
        }

        // Plain navigation to the presigned URL — S3's Content-Disposition
        // header triggers the download with the correct filename.
        const link = document.createElement("a");
        link.href = result.download_url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        handleToastOpen("success", "Excel File Downloaded Successfully");
      } else if (selectedTool === "Classification") {
        // ═══ S3 presigned-URL flow (manual Classification only) ═══
        const result = await downloadClassificationOutputFile(
          selectedTool,
          batchId,
        );

        if (!result?.download_url) {
          throw new Error("Server did not return a download URL");
        }

        const link = document.createElement("a");
        link.href = result.download_url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        handleToastOpen("success", "Excel File Downloaded Successfully");
      } else {
        // ═══ Existing base64 flow for every other tool — unchanged ═══
        const downloadData = await downloadProcessedData(
          selectedTool,
          batchId,
          modelForDownload,
        );
        const formatName = (name) => (name ? name.replace(/[\s-]+/g, "_") : "");
        const filename = `${formatName(selectedTool)}_${formatName(modelForDownload)}_${new Date()
          .toISOString()
          .replace(/[-:]/g, "")
          .slice(0, 15)
          .replace("T", "_")}.xlsx`;
        const binaryString = atob(downloadData.base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++)
          bytes[i] = binaryString.charCodeAt(i);
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
        handleToastOpen("success", "Excel File Downloaded Successfully");
      }
    } catch (error) {
      if (error.name !== "AbortError")
        handleToastOpen(
          "error",
          "Excel File Download Failed: " + (error.message || "Unknown error"),
        );
    } finally {
      setCOpen(false);
      setTimeout(() => handleViewHistory(), 500);
    }
  };
  const handleReset = () => {
    const params = new URLSearchParams(location.search);
    if (params.get("toBeProcessed") === "true") {
      navigate(-1);
    } else {
      setSelectedTool(toolOptions[toolTabValue].name);
      setSelectedFile(null);
      setFileUploaded(false);
      setIsProcessingComplete(false);
      setActiveStep(1);
      setUniqueRecords(0);
      setProcessRecords(0);
      setTotalCount(0);
      setModelSelectionType("existing");
      setUploadedModelFile(null);
      setUploadedModelS3Location(null);
      handleToastOpen("Process reset", "");
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  useEffect(() => {
    setSelectedModel("");
    setModelSelectionType("existing");
    setUploadedModelFile(null);
    setUploadedModelS3Location(null);
  }, [selectedTool]);

  const handleToolTabChange = (event, newValue) => {
    const tabIndex =
      typeof newValue === "number"
        ? newValue
        : event && typeof event === "number"
          ? event
          : 0;
    if (tabConfig[tabIndex].disabled) {
      handleToastOpen(
        `${tabConfig[tabIndex].label} tool is currently Work In Progress`,
        "info",
      );
      return;
    }
    setModelOptions([]);
    setSelectedModel("");
    setSelectedCustomer("");
    setModelSelectionType("existing");
    setUploadedModelFile(null);
    setUploadedModelS3Location(null);
    setToolTabValue(tabIndex);
    setSelectedTool(toolOptions[tabIndex].name);
    setActiveStep(1);
    setFileUploaded(false);
    setIsProcessingComplete(false);
    setSelectedFile(null);
    setUniqueRecords(0);
    setProcessRecords(0);
    setTotalCount(0);
    setAutomationPlantId("");
    setAutomationStartDate("");
    setAutomationEndDate("");
    setAutomationModelId("");
    setAutomationInputId("");
    setClassificationLangauge("");
    setClassificationLangaugeOptions([]);
    setAutomationModelOptions([]);
    setAutomationInputOptions([]);
    setAutomationFileStats({ totalRecords: 0, uniqueRecords: 0 });
    setIsAutomationComplete(false);
    setAutomationBatchId(null);
    handleToastOpen(`Switched to ${toolOptions[tabIndex].name} tool`, "");
  };

  const handleModelChange = (event) => setSelectedModel(event);
  const handleCustomerChange = (event) => setSelectedCustomer(event);
  const handleViewHistory = () =>
    navigate(
      `/data-operation-centre/history?tool=${encodeURIComponent(selectedTool)}`,
    );

  const countUniqueRecords = async (file) => {
    if (!file) return 0;
    const fileObject = Array.isArray(file) ? file[0] : file;
    if (!fileObject) return 0;
    const fileType = fileObject.type;
    const fileName = fileObject.name || "";
    let columnToSearch = "";
    if (selectedTool === "Data Matching") columnToSearch = "Int. material no.";
    else if (selectedTool === "Classification") columnToSearch = "Material";
    const isExcel =
      fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls");
    if (!isExcel) return 0;
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = function (event) {
        try {
          const workbook = XLSX.read(event.target.result, {
            type: "array",
            cellDates: true,
          });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(sheet, {
            defval: null,
            blankrows: false,
          });
          let uniqueCount = 0;
          if (data.length > 0) {
            const headers = Object.keys(data[0]);
            if (selectedTool === "Enrichment") {
              let materialNoField =
                headers.find(
                  (h) =>
                    typeof h === "string" && h.toLowerCase() === "material no",
                ) ||
                headers.find(
                  (h) =>
                    typeof h === "string" &&
                    h.toLowerCase().includes("material") &&
                    h.toLowerCase().includes("no"),
                );
              let projectNameField =
                headers.find(
                  (h) =>
                    typeof h === "string" && h.toLowerCase() === "project name",
                ) ||
                headers.find(
                  (h) =>
                    typeof h === "string" &&
                    h.toLowerCase().includes("project") &&
                    h.toLowerCase().includes("name"),
                );
              if (materialNoField && projectNameField) {
                const uniqueCombinations = new Set();
                data.forEach((row) => {
                  const materialNo = row[materialNoField];
                  const projectName = row[projectNameField];
                  if (materialNo != null && projectName != null) {
                    const nm = String(materialNo).trim();
                    const np = String(projectName).trim();
                    if (nm && np) uniqueCombinations.add(`${nm}|${np}`);
                  }
                });
                uniqueCount = uniqueCombinations.size;
              } else uniqueCount = data.length;
            } else {
              let targetField = null;
              if (selectedTool === "Classification") {
                targetField =
                  headers.find(
                    (h) =>
                      typeof h === "string" && h.toLowerCase() === "material",
                  ) ||
                  headers.find(
                    (h) =>
                      typeof h === "string" &&
                      h.toLowerCase().includes("material"),
                  );
              } else {
                targetField =
                  headers.find(
                    (h) =>
                      typeof h === "string" &&
                      h.toLowerCase() === columnToSearch.toLowerCase(),
                  ) ||
                  headers.find(
                    (h) =>
                      typeof h === "string" &&
                      h.toLowerCase().includes(columnToSearch.toLowerCase()),
                  );
              }
              if (targetField && data.length > 0) {
                const uniqueValues = new Set();
                data.forEach((row) => {
                  if (row[targetField] != null) {
                    const v = String(row[targetField]).trim();
                    if (v) uniqueValues.add(v);
                  }
                });
                uniqueCount = uniqueValues.size;
              } else uniqueCount = data.length > 0 ? data.length : 0;
            }
          }
          setUniqueRecords(uniqueCount);
          resolve(uniqueCount);
        } catch (error) {
          console.error("Error processing file:", error);
          reject(error);
        }
      };
      reader.onerror = function () {
        reject("FileReader error");
      };
      try {
        reader.readAsArrayBuffer(fileObject);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleFileSelection = async (files) => {
    if (selectedTool !== "Duplicate Check") {
      if (selectedTool === "Enrichment") {
        if (!selectedCustomer || selectedCustomer === "") {
          handleToastOpen("error", "Please select a customer name first");
          return;
        }
        setEnrichBatchId(uuidv4());
      } else if (selectedTool === "Data Matching") {
        if (!isModelSelected()) {
          handleToastOpen(
            "error",
            "Please select a model first to enable upload",
          );
          return;
        }
      } else {
        if (!selectedModel || selectedModel === "") {
          handleToastOpen("error", "Please select a model name first");
          return;
        }
      }
    }
    if (!files || files.length === 0) return;
    const file = files[0];
    const validMimeTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const validExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (
      !validMimeTypes.includes(file.type) ||
      !validExtensions.includes(fileExtension)
    ) {
      handleToastOpen(
        "error",
        "Invalid file type. Please upload an Excel (.xlsx or .xls) file.",
      );
      return;
    }
    setErrorDetails(null);
    setShowErrorModal(false);
    setSelectedFile(file);
    setCOpen(true);
    try {
      const getRecordLimit = (tool) => {
        switch (tool) {
          case "Classification":
            return 70000;
          case "Data Matching":
            return 100000;
          case "Enrichment":
            return 5000;
          case "Mpn Segregation":
            return 35000;
          case "alternate Manufacturer":
            return 1000;
          case "Duplicate Check":
            return 50000;
          default:
            return 50000;
        }
      };
      const recordLimit = getRecordLimit(selectedTool);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const allRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const rowsWithoutHeader = allRows
            .slice(1)
            .filter((row) => row.length > 0);
          const recordCount = rowsWithoutHeader.length;
          if (recordCount > recordLimit) {
            setSelectedFile("");
            handleToastOpen(
              "error",
              `File contains ${recordCount} records. Maximum allowed is ${recordLimit}.`,
            );
          } else {
            setTotalCount(recordCount);
            handleToastOpen("success", `Selected file: ${file.name}`);
          }
        } catch (err) {
          handleToastOpen("error", `Error processing file: ${err.message}`);
        } finally {
          setCOpen(false);
        }
      };
      reader.onerror = () => {
        handleToastOpen("error", "Error reading file.");
        setCOpen(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      handleToastOpen("error", error.message);
      setCOpen(false);
    }
  };

  const validateFile = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject("No file selected.");
        return;
      }
      const validMimeTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const validExtensions = ["xlsx", "xls"];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!validMimeTypes.includes(file.type)) {
        reject(
          "Invalid file type. Please upload an Excel (.xlsx or .xls) file.",
        );
        return;
      }
      if (!validExtensions.includes(fileExtension)) {
        reject(
          "Invalid file extension. Please upload an Excel (.xlsx or .xls) file.",
        );
        return;
      }
      const rl = {
        Classification: 70000,
        "Data Matching": 100000,
        Enrichment: 5000,
        "Mpn Segregation": 35000,
        "alternate Manufacturer": 1000,
        "Duplicate Check": 50000,
      };
      const recordLimit = rl[selectedTool] || 50000;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const jsonData = XLSX.utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
          );
          if (jsonData.length > recordLimit) {
            reject(
              `File contains ${jsonData.length} records. Maximum allowed is ${recordLimit} records.`,
            );
            return;
          }
          resolve(null);
        } catch (error) {
          reject(`Error processing file: ${error.message}`);
        }
      };
      reader.onerror = () => reject("Error reading file.");
      reader.readAsArrayBuffer(file);
    });
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getToolTitle = () =>
    selectedTool || toolOptions[toolTabValue]?.name || "Data Processing";

  const params = new URLSearchParams(location.search);
  const urlBatchId = params.get("batchId");
  const urlToolName = params.get("toolName");
  const urlModelName = params.get("modelName");
  const urlTotalRecords = params.get("totalRecords")
    ? parseInt(params.get("totalRecords"), 10)
    : null;
  const urlUniqueRecords = params.get("uniqueRecords")
    ? parseInt(params.get("uniqueRecords"), 10)
    : null;
  const urlToBeProcessed = params.get("toBeProcessed") === "true";

  useEffect(() => {
    if (urlBatchId && urlToBeProcessed) {
      if (urlToolName) {
        const tabIndex = getToolTabIndexFromName(urlToolName);
        if (!tabConfig[tabIndex]?.disabled) {
          setToolTabValue(tabIndex);
          setSelectedTool(urlToolName);
        }
      }
      setBatchId(urlBatchId);
      setActiveStep(2);
      setFileUploaded(true);
      if (urlTotalRecords) setTotalCount(urlTotalRecords);
      if (urlUniqueRecords) setUniqueRecords(urlUniqueRecords);
      if (urlModelName) setSelectedModel(urlModelName);
      else if (urlToolName) {
        if (urlToolName === "Classification") setSelectedModel("ICTSI");
        else if (urlToolName === "Data Matching")
          setSelectedModel("ICTSI-GT00");
      }
      handleToastOpen("success", "File Retrieved Successfully");
    }
  }, []);

  const handleErrorDialogClose = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
  };

  const handleModelFileUpload = async () => {
    if (!uploadedModelFile) {
      handleToastOpen("error", "No model file selected");
      return;
    }
    try {
      setIsModelFileUploading(true);
      const uploadBatchIdToUse = uuidv4();
      const modelS3Location = await uploadModelFileToS3(
        uploadedModelFile,
        uploadBatchIdToUse,
        selectedTool,
      );
      setBatchId(modelS3Location.batch_id);
      setUploadedModelS3Location(modelS3Location);
      handleToastOpen(
        "success",
        `Model file uploaded successfully: ${uploadedModelFile.name}`,
      );
    } catch (error) {
      console.error("Model file upload error:", error);
      handleToastOpen("error", error.message || "Failed to upload model file");
    } finally {
      setIsModelFileUploading(false);
    }
  };

  return {
    activeStep,
    tabValue,
    toolTabValue,
    selectedTool,
    selectedFile,
    fileUploaded,
    isProcessing,
    isProcessingComplete,
    selectedModel,
    showModelDropdown,
    processingProgress,
    processingStats,
    isMobile,
    totalCount,
    isUploading,
    uploadProgress,
    batchId,
    errorDetails,
    showErrorModal,
    modelOptions,
    cardData,
    toolOptions,
    cOpen,
    tabConfig,
    selectedCustomer,
    isLoadingOptions,
    sampleDialogOpen,
    selectedSampleType,
    sampleDownloadLoading,
    sampleTypeOptions,
    showInputSubOptions,
    selectedInputSubType,
    inputSubTypeOptions,
    modelSelectionType,
    uploadedModelFile,
    isModelFileUploading,
    modelSelectionOptions,
    isModelSelected,
    uploadedModelS3Location,
    automationPlantId,
    automationStartDate,
    automationEndDate,
    isAutomationProcessing,
    setActiveStep,
    setTabValue,
    setToolTabValue,
    setSelectedTool,
    setSelectedFile,
    setFileUploaded,
    setIsProcessing,
    setIsProcessingComplete,
    setSelectedModel,
    setShowModelDropdown,
    setProcessingProgress,
    setProcessingStats,
    setCOpen,
    setSampleDialogOpen,
    setSelectedSampleType,
    setUploadedModelFile,
    setUploadedModelS3Location,
    setAutomationPlantId,
    setAutomationStartDate,
    setAutomationEndDate,
    handleFileUpload,
    handleStartProcessing,
    handleDownload,
    handleRequestSample,
    handleReset,
    handleTabChange,
    handleToolTabChange,
    handleModelChange,
    handleCustomerChange,
    handleViewHistory,
    handleFileSelection,
    handleErrorDialogClose,
    handleSampleDialogClose,
    handleConfirmSampleDownload,
    handleSampleTypeSelection,
    handleInputSubTypeSelection,
    handleModelSelectionTypeChange,
    handleModelFileSelection,
    handleModelFileUpload,
    handleAutomationProcess,
    getToolTitle,
    clearAllIntervals,
    plantOptions,
    isLoadingPlantOptions,
    automationFileStats,
    isAutomationComplete,
    automationBatchId,
    handleAutomationDownload,
    handleAutomationRefresh,
    isLoadingAutomationCount,
    automationModelId,
    setAutomationModelId,
    automationModelOptions,
    isE2EToolWithMandatoryModel,
    automationInputId,
    setAutomationInputId,
    automationInputOptions,
    classificationLangauge,
    setClassificationLangauge,
    classificationLangaugeOptions,
    handleFetchAutomationCount,
  };
};

export default DataProcessingVM;
